import React, { useState, useEffect } from 'react';
import { 
  Database, 
  Coins, 
  TrendingUp, 
  AlertTriangle, 
  Plus, 
  Trash2,
  FileSpreadsheet,
  Download,
  Info,
  Scale,
  CheckCircle2,
  ShieldCheck,
  Send,
  FileText,
  Lock,
  ArrowRight,
  Terminal,
  Activity,
  History,
  FileCode,
  Sparkles,
  User,
  ShieldAlert,
  Sliders,
  DollarSign,
  Play,
  ChevronRight,
  Search,
  RefreshCw,
  SlidersHorizontal,
  Eye,
  Layers,
  CheckCircle,
  Ban,
  HelpCircle,
  Heart,
  Settings,
  CheckSquare
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { NexusTaxReadinessHub } from './NexusTaxReadinessHub';
import { NexusPrivacyConsentCenter } from './NexusPrivacyConsentCenter';

// Interfaces for our Two-Layer structure

// Layer 1 Metering Entry
export interface MeteringEvent {
  event_id: string;
  event_type: 'NEXUS_SEARCH_QUERY' | 'CACHED_SEARCH' | 'FAILED_SEARCH' | 'SEARCH_CREDIT_PURCHASE' | 'SEARCH_CREDIT_CONSUMPTION' | 'NEXUS_CHAT_CONVO' | 'NEXUS_AGENT_RUN' | 'WORKSUITE_PROJECT' | 'MEDIA_RENDER' | 'VIDEO_RENDER' | 'SOCIAL_POST_SCHED' | 'HOSTING_BANDWIDTH' | 'HOSTING_STORAGE' | 'MAILBOX_CREATED' | 'EMAIL_SENT' | 'NEWSLETTER_SENT' | 'DOMAIN_SETUP' | 'LAB_NOTEBOOK' | 'LAB_AI_TASK' | 'APP_BUILDER_PROJ';
  workspace_id: string;
  user_id: string;
  product_module: string;
  quantity: number;
  unit_price: number;
  currency: string;
  credit_balance_before: number;
  credit_balance_after: number;
  billable_true_false: boolean;
  billing_status: 'METERED' | 'BILLED' | 'EXCLUDED' | 'PENDING_REVIEW';
  source_action: string;
  created_at_utc: string;
  approval_required: boolean;
  approval_status: 'AUTO_APPROVED' | 'PENDING' | 'REJECTED' | 'MANUAL_APPROVED';
}

// Layer 2 Accounting Ledger Journal
export interface AccountingJournal {
  journal_id: string;
  transaction_id: string;
  invoice_id: string;
  debit_account: string;
  credit_account: string;
  amount: number;
  currency: string;
  VAT_status: 'VAT_NOT_REGISTERED' | 'VAT_STANDARD_20' | 'VAT_ZERO_RATED' | 'VAT_EXEMPT';
  VAT_rate: number;
  VAT_amount: number;
  payment_provider_reference: string;
  created_at_utc: string;
  locked_status: boolean;
  correction_status: 'ORIGINAL' | 'CORRECTED_REVERSING' | 'ADJUSTMENT';
  audit_hash: string;
  accountant_review_status: 'UNREVIEWED' | 'APPROVED' | 'DISPUTED';
}

// Invoices & Receipts
export interface InvoiceRecord {
  invoice_number: string;
  customer_name: string;
  customer_email: string;
  company_name: string;
  billing_address: string;
  country: string;
  product_service: string;
  plan: string;
  billing_period: string;
  base_price: number;
  search_credits: number;
  add_ons: number;
  maintenance_hosting_fee: number;
  VAT_status: 'VAT_NOT_CHARGED' | 'VAT_20';
  VAT_amount: number;
  total: number;
  payment_status: 'PAID' | 'UNPAID' | 'VOID' | 'REFUNDED';
  provider_reference: string;
  issue_date: string;
  due_date: string;
  paid_date: string;
  refund_status: 'NONE' | 'PARTIAL' | 'FULL';
  receipt_link: string;
}

// Payment Reconciliations
export interface PaymentReconciliation {
  payment_id: string;
  provider: 'STRIPE' | 'PAYPAL' | 'APPLE_PAY' | 'BANK_TRANSFER';
  provider_reference: string;
  gross_amount: number;
  fee_amount: number;
  net_received: number;
  currency: string;
  invoice_id: string;
  customer_id: string;
  subscription_id: string;
  status: 'Matched' | 'Pending' | 'Mismatch' | 'Needs Review' | 'Refunded' | 'Chargeback' | 'Failed';
  payout_status: 'PENDING' | 'PAID_OUT' | 'HELD';
  matched_ledger_entry: string;
  mismatch_warning: string;
  reconciliation_status: 'RECONCILED' | 'UNRECONCILED' | 'MANUALLY_ADJUSTED';
}

export function NexusAccountingSystem() {
  // Roles for visibility checks
  const [activeRole, setActiveRole] = useState<'SUPER_ADMIN' | 'ADMIN' | 'CLIENT' | 'PUBLIC_VISITOR'>('SUPER_ADMIN');

  // Currently active tab depending on role permission
  const [activeTab, setActiveTab] = useState<string>('METERING');

  // System settings
  const [vatRegistered, setVatRegistered] = useState<boolean>(false);
  const [vatNumber, setVatNumber] = useState<string>('GB 982 7631 05');
  const [vatEffectiveDate, setVatEffectiveDate] = useState<string>('2026-01-01');
  const [customVATRate, setCustomVATRate] = useState<number>(20);
  
  // Turnover state for progressive limits
  const [rolling12mTurnover, setRolling12mTurnover] = useState<number>(72500); // Exceeds 85k triggers VAT register standard
  const [expected30dayTurnover, setExpected30dayTurnover] = useState<number>(12000);
  const [voluntaryRegistration, setVoluntaryRegistration] = useState<boolean>(false);

  // States for search credit events simulation
  const [searchQuerySeed, setSearchQuerySeed] = useState<string>('');
  const [searchHistory, setSearchHistory] = useState<{ query: string; timestamp: number; hash: string }[]>([]);
  const [searchCreditsBalance, setSearchCreditsBalance] = useState<number>(500);

  // Metering events list (Layer 1)
  const [meteringEvents, setMeteringEvents] = useState<MeteringEvent[]>(() => {
    const cached = localStorage.getItem('nexus_metering_events');
    if (cached) {
      try { return JSON.parse(cached); } catch(e) {}
    }
    // High-fidelity pre-seed events
    return [
      {
        event_id: "evt_srch_10931",
        event_type: 'NEXUS_SEARCH_QUERY',
        workspace_id: "wksp_alpha_991",
        user_id: "usr_mahmood_Director",
        product_module: "Search Vector Engine",
        quantity: 1,
        unit_price: 0.01,
        currency: "GBP",
        credit_balance_before: 500,
        credit_balance_after: 499,
        billable_true_false: true,
        billing_status: 'METERED',
        source_action: "User triggered search query: 'NexoraX billing records criteria'",
        created_at_utc: "2026-06-14T11:21:40Z",
        approval_required: false,
        approval_status: 'AUTO_APPROVED'
      },
      {
        event_id: "evt_srch_10932",
        event_type: 'CACHED_SEARCH',
        workspace_id: "wksp_alpha_991",
        user_id: "usr_mahmood_Director",
        product_module: "Search Vector Engine",
        quantity: 0,
        unit_price: 0.00,
        currency: "GBP",
        credit_balance_before: 499,
        credit_balance_after: 499,
        billable_true_false: false,
        billing_status: 'EXCLUDED',
        source_action: "Duplicate search within 60s cache window: 'NexoraX billing records criteria'",
        created_at_utc: "2026-06-14T11:22:15Z",
        approval_required: false,
        approval_status: 'AUTO_APPROVED'
      },
      {
        event_id: "evt_srch_10933",
        event_type: 'FAILED_SEARCH',
        workspace_id: "wksp_alpha_991",
        user_id: "usr_mahmood_Director",
        product_module: "Search Vector Engine",
        quantity: 0,
        unit_price: 0.00,
        currency: "GBP",
        credit_balance_before: 499,
        credit_balance_after: 499,
        billable_true_false: false,
        billing_status: 'EXCLUDED',
        source_action: "Query timed out due to index segment offline",
        created_at_utc: "2026-06-14T11:25:02Z",
        approval_required: false,
        approval_status: 'AUTO_APPROVED'
      },
      {
        event_id: "evt_agnt_20311",
        event_type: 'NEXUS_AGENT_RUN',
        workspace_id: "wksp_beta_887",
        user_id: "usr_Staff Coordinator_remote",
        product_module: "Cognitive AI Agent Engine",
        quantity: 1,
        unit_price: 0.05,
        currency: "GBP",
        credit_balance_before: 120,
        credit_balance_after: 115,
        billable_true_false: true,
        billing_status: 'METERED',
        source_action: "Autonomous pipeline sweep for bank feed match",
        created_at_utc: "2026-06-13T16:04:12Z",
        approval_required: false,
        approval_status: 'AUTO_APPROVED'
      },
      {
        event_id: "evt_mda_30221",
        event_type: 'VIDEO_RENDER',
        workspace_id: "wksp_gamma_442",
        user_id: "usr_Communications Associate_comms",
        product_module: "Spectacle Media Engine",
        quantity: 1,
        unit_price: 0.50,
        currency: "GBP",
        credit_balance_before: 350,
        credit_balance_after: 349,
        billable_true_false: true,
        billing_status: 'METERED',
        source_action: "Premium mp4 high-definition video render",
        created_at_utc: "2026-06-12T09:12:35Z",
        approval_required: true,
        approval_status: 'MANUAL_APPROVED'
      }
    ];
  });

  // Accounting journals list (Layer 2)
  const [accountingJournals, setAccountingJournals] = useState<AccountingJournal[]>(() => {
    const cached = localStorage.getItem('nexus_accounting_journals');
    if (cached) {
      try { return JSON.parse(cached); } catch(e) {}
    }
    return [
      {
        journal_id: "jn_1001_sales",
        transaction_id: "tx_99831a",
        invoice_id: "INV-2026-001",
        debit_account: "Accounts Receivable",
        credit_account: "Sales Revenue",
        amount: 250.00,
        currency: "GBP",
        VAT_status: 'VAT_NOT_REGISTERED',
        VAT_rate: 0,
        VAT_amount: 0.00,
        payment_provider_reference: "ch_stripe_8f7b2a9",
        created_at_utc: "2026-06-14T10:00:00Z",
        locked_status: true,
        correction_status: 'ORIGINAL',
        audit_hash: "2e9a5c4e12fa28ffcd63aa2",
        accountant_review_status: 'UNREVIEWED'
      },
      {
        journal_id: "jn_1001_cash",
        transaction_id: "tx_99831a",
        invoice_id: "INV-2026-001",
        debit_account: "Cash / Bank (Provider Clearing)",
        credit_account: "Accounts Receivable",
        amount: 250.00,
        currency: "GBP",
        VAT_status: 'VAT_NOT_REGISTERED',
        VAT_rate: 0,
        VAT_amount: 0.00,
        payment_provider_reference: "ch_stripe_8f7b2a9",
        created_at_utc: "2026-06-14T10:01:15Z",
        locked_status: true,
        correction_status: 'ORIGINAL',
        audit_hash: "f4da12bd30be347209da3e",
        accountant_review_status: 'APPROVED'
      },
      {
        journal_id: "jn_1002_subs",
        transaction_id: "tx_99832b",
        invoice_id: "INV-2026-002",
        debit_account: "Cash / Bank (Provider Clearing)",
        credit_account: "Subscription Revenue",
        amount: 149.00,
        currency: "GBP",
        VAT_status: 'VAT_NOT_REGISTERED',
        VAT_rate: 0,
        VAT_amount: 0.00,
        payment_provider_reference: "ch_stripe_8e2k38s",
        created_at_utc: "2026-06-13T12:00:00Z",
        locked_status: true,
        correction_status: 'ORIGINAL',
        audit_hash: "98e4d3a2b10376cfd3892",
        accountant_review_status: 'UNREVIEWED'
      },
      {
        journal_id: "jn_1003_host",
        transaction_id: "tx_99833c",
        invoice_id: "INV-2026-003",
        debit_account: "Cash / Bank (Provider Clearing)",
        credit_account: "Maintenance & Hosting Fee Revenue",
        amount: 80.00,
        currency: "GBP",
        VAT_status: 'VAT_NOT_REGISTERED',
        VAT_rate: 0,
        VAT_amount: 0.00,
        payment_provider_reference: "ch_stripe_4k83s1a",
        created_at_utc: "2026-06-12T14:30:00Z",
        locked_status: true,
        correction_status: 'ORIGINAL',
        audit_hash: "12ab23cd34ef45df56gh78",
        accountant_review_status: 'UNREVIEWED'
      },
      {
        journal_id: "jn_1004_refnd",
        transaction_id: "tx_99834r",
        invoice_id: "INV-2026-001",
        debit_account: "Sales Revenue",
        credit_account: "Cash / Bank (Provider Clearing)",
        amount: 50.00,
        currency: "GBP",
        VAT_status: 'VAT_NOT_REGISTERED',
        VAT_rate: 0,
        VAT_amount: 0.00,
        payment_provider_reference: "re_stripe_f8s2kx8",
        created_at_utc: "2026-06-14T15:15:00Z",
        locked_status: true,
        correction_status: 'CORRECTED_REVERSING',
        audit_hash: "9aa88c7d6e5f3211a00bc32",
        accountant_review_status: 'UNREVIEWED'
      }
    ];
  });

  // Invoices & Receipts register
  const [invoices, setInvoices] = useState<InvoiceRecord[]>(() => {
    const cached = localStorage.getItem('nexus_invoices');
    if (cached) {
      try { return JSON.parse(cached); } catch(e) {}
    }
    return [
      {
        invoice_number: "INV-2026-001",
        customer_name: "Eleanor Vance",
        customer_email: "eleanor.vance@vanguardcomms.co.uk",
        company_name: "Vanguard Comms Ltd",
        billing_address: "Standard House, Dunfermline, Fife, Scotland",
        country: "United Kingdom",
        product_service: "B2B SaaS Singularity Membership",
        plan: "Zenith Executive Pro",
        billing_period: "June 2026 - Monthly",
        base_price: 250.00,
        search_credits: 2000,
        add_ons: 0,
        maintenance_hosting_fee: 15.00,
        VAT_status: 'VAT_NOT_CHARGED',
        VAT_amount: 0.00,
        total: 265.00,
        payment_status: 'PAID',
        provider_reference: "ch_stripe_8f7b2a9",
        issue_date: "2026-06-14",
        due_date: "2026-06-21",
        paid_date: "2026-06-14",
        refund_status: 'NONE',
        receipt_link: "https://pay.stripe.com/receipts/acct_109s_INV001"
      },
      {
        invoice_number: "INV-2026-002",
        customer_name: "Helen Miller",
        customer_email: "helen.m@nexusconsulting.net",
        company_name: "Miller Consulting Group",
        billing_address: "14 Lothian Chambers, Edinburgh",
        country: "United Kingdom",
        product_service: "Nexus OS Enterprise Seat",
        plan: "VIP Access Plan",
        billing_period: "June 2026 - Monthly",
        base_price: 149.00,
        search_credits: 1000,
        add_ons: 0,
        maintenance_hosting_fee: 10.00,
        VAT_status: 'VAT_NOT_CHARGED',
        VAT_amount: 0.00,
        total: 159.00,
        payment_status: 'PAID',
        provider_reference: "ch_stripe_8e2k38s",
        issue_date: "2026-06-13",
        due_date: "2026-06-20",
        paid_date: "2026-06-13",
        refund_status: 'NONE',
        receipt_link: "https://pay.stripe.com/receipts/acct_109s_INV002"
      },
      {
        invoice_number: "INV-2026-003",
        customer_name: "Claire Jenkins",
        customer_email: "claire@stratcomms.co.uk",
        company_name: "Strategic Communications UK",
        billing_address: "22 Threadneedle St, London",
        country: "United Kingdom",
        product_service: "Cloud Node Cluster Lease + Domains",
        plan: "Multi-cluster Host Plan",
        billing_period: "June 2026 - Monthly",
        base_price: 80.00,
        search_credits: 500,
        add_ons: 15.00,
        maintenance_hosting_fee: 10.00,
        VAT_status: 'VAT_NOT_CHARGED',
        VAT_amount: 0.00,
        total: 105.00,
        payment_status: 'PAID',
        provider_reference: "ch_stripe_4k83s1a",
        issue_date: "2026-06-12",
        due_date: "2026-06-19",
        paid_date: "2026-06-12",
        refund_status: 'NONE',
        receipt_link: "https://pay.stripe.com/receipts/acct_109s_INV003"
      },
      {
        invoice_number: "INV-2026-004",
        customer_name: "Alistair G.",
        customer_email: "alistair@northgrid.org",
        company_name: "NorthGrid Solutions",
        billing_address: "7 York Place, Leeds",
        country: "United Kingdom",
        product_service: "Spectacle Custom AI Video Bundle",
        plan: "Enterprise Creator",
        billing_period: "June 2026 - Instant",
        base_price: 450.00,
        search_credits: 5000,
        add_ons: 100.00,
        maintenance_hosting_fee: 30.00,
        VAT_status: 'VAT_NOT_CHARGED',
        VAT_amount: 0.00,
        total: 580.00,
        payment_status: 'UNPAID',
        provider_reference: "",
        issue_date: "2026-06-14",
        due_date: "2026-06-28",
        paid_date: "",
        refund_status: 'NONE',
        receipt_link: ""
      }
    ];
  });

  // Reconciliations
  const [reconciliations, setReconciliations] = useState<PaymentReconciliation[]>(() => {
    const cached = localStorage.getItem('nexus_reconciliations');
    if (cached) {
      try { return JSON.parse(cached); } catch(e) {}
    }
    return [
      {
        payment_id: "pay_strp_00911",
        provider: 'STRIPE',
        provider_reference: "ch_stripe_8f7b2a9",
        gross_amount: 265.00,
        fee_amount: 7.95,
        net_received: 257.05,
        currency: "GBP",
        invoice_id: "INV-2026-001",
        customer_id: "usr_eleanor_v",
        subscription_id: "sub_zenith_121",
        status: 'Matched',
        payout_status: 'PAID_OUT',
        matched_ledger_entry: "jn_1001_cash",
        mismatch_warning: "",
        reconciliation_status: 'RECONCILED'
      },
      {
        payment_id: "pay_strp_00912",
        provider: 'STRIPE',
        provider_reference: "ch_stripe_8e2k38s",
        gross_amount: 159.00,
        fee_amount: 4.77,
        net_received: 154.23,
        currency: "GBP",
        invoice_id: "INV-2026-002",
        customer_id: "usr_helen_m",
        subscription_id: "sub_vip_222",
        status: 'Matched',
        payout_status: 'PAID_OUT',
        matched_ledger_entry: "jn_1002_subs",
        mismatch_warning: "",
        reconciliation_status: 'RECONCILED'
      },
      {
        payment_id: "pay_strp_00913",
        provider: 'STRIPE',
        provider_reference: "ch_stripe_4k83s1a",
        gross_amount: 105.00,
        fee_amount: 3.15,
        net_received: 101.85,
        currency: "GBP",
        invoice_id: "INV-2026-003",
        customer_id: "usr_claire_j",
        subscription_id: "sub_host_333",
        status: 'Matched',
        payout_status: 'PENDING',
        matched_ledger_entry: "jn_1003_host",
        mismatch_warning: "",
        reconciliation_status: 'RECONCILED'
      },
      {
        payment_id: "pay_strp_00914",
        provider: 'STRIPE',
        provider_reference: "ch_stripe_f8ss23s",
        gross_amount: 120.00,
        fee_amount: 3.60,
        net_received: 116.40,
        currency: "GBP",
        invoice_id: "INV-2026-999", // Unregistered invoice
        customer_id: "usr_unknown",
        subscription_id: "sub_erroneous",
        status: 'Mismatch',
        payout_status: 'HELD',
        matched_ledger_entry: "NONE",
        mismatch_warning: "No matching invoice found in register folder",
        reconciliation_status: 'UNRECONCILED'
      },
      {
        payment_id: "pay_strp_00915",
        provider: 'STRIPE',
        provider_reference: "ch_stripe_8f4kf92",
        gross_amount: 50.00,
        fee_amount: 1.50,
        net_received: 48.50,
        currency: "GBP",
        invoice_id: "INV-2026-001",
        customer_id: "usr_eleanor_v",
        subscription_id: "sub_zenith_121",
        status: 'Refunded',
        payout_status: 'PAID_OUT',
        matched_ledger_entry: "jn_1004_refnd",
        mismatch_warning: "",
        reconciliation_status: 'RECONCILED'
      }
    ];
  });

  // Future MTD Settings
  const [mtdStatus, setMtdStatus] = useState<'NOT_CONNECTED' | 'PLANNING' | 'SANDBOX_TESTING' | 'DEV_TESTING' | 'DISABLED_PREVIEW'>('DISABLED_PREVIEW');

  // Community Allocation state
  const [communityCalculationPeriod, setCommunityCalculationPeriod] = useState<string>('Q2 2026 Plan');
  const [communityApprovedByAccountant, setCommunityApprovedByAccountant] = useState<boolean>(true);
  const [communityPaidAllocation, setCommunityPaidAllocation] = useState<number>(100.52);

  const [beneficiary1Name, setBeneficiary1Name] = useState<string>("");
  const [beneficiary1Percent, setBeneficiary1Percent] = useState<string>("1%");
  const [beneficiary2Name, setBeneficiary2Name] = useState<string>("");
  const [beneficiary2Percent, setBeneficiary2Percent] = useState<string>("1%");
  const [beneficiary3Name, setBeneficiary3Name] = useState<string>("");
  const [beneficiary3Percent, setBeneficiary3Percent] = useState<string>("1%");
  const [calculationBasis, setCalculationBasis] = useState<string>("");
  const [vatExcluded, setVatExcluded] = useState<string>("Yes");
  const [refundsExcluded, setRefundsExcluded] = useState<string>("Yes");
  const [chargebacksExcluded, setChargebacksExcluded] = useState<string>("Yes");
  const [timePeriod, setTimePeriod] = useState<string>("");
  const [contributionCap, setContributionCap] = useState<string>("");
  const [publicDisclosureEnabled, setPublicDisclosureEnabled] = useState<boolean>(false);
  const [communityReviewStatus, setCommunityReviewStatus] = useState<string>("Pending");

  // Admin-only Company Tax Identity state
  const [companyLegalName, setCompanyLegalName] = useState<string>("NexoraX Digital Ltd");
  const [companyNumber, setCompanyNumber] = useState<string>("SC887095");
  const [companyUTR, setCompanyUTR] = useState<string>("7095282046");
  const [govGatewayStatus, setGovGatewayStatus] = useState<string>("Sandbox / Sandbox Mode Only");
  const [corpTaxEnrolment, setCorpTaxEnrolment] = useState<string>("Pending Development Sandbox");
  const [vatRegStatus, setVatRegStatus] = useState<string>("Not Registered");
  const [payeRegStatus, setPayeRegStatus] = useState<string>("Sandbox Configured");
  const [selfAssessmentStatus, setSelfAssessmentStatus] = useState<string>("Self-managed Record Sandbox");
  const [accountantReviewStatus, setAccountantReviewStatus] = useState<string>("Review Required");

  // States for manual ledger adjustment modal / form
  const [newJournalDebitAccount, setNewJournalDebitAccount] = useState<string>('');
  const [newJournalCreditAccount, setNewJournalCreditAccount] = useState<string>('');
  const [newJournalAmount, setNewJournalAmount] = useState<number>(0);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState<boolean>(false);

  // Toast notifier
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'info' | 'error' } | null>(null);

  const showToastMsg = (message: string, type: 'success' | 'info' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Auto-synchronize back into localStorage
  useEffect(() => {
    localStorage.setItem('nexus_metering_events', JSON.stringify(meteringEvents));
  }, [meteringEvents]);

  useEffect(() => {
    localStorage.setItem('nexus_accounting_journals', JSON.stringify(accountingJournals));
  }, [accountingJournals]);

  useEffect(() => {
    localStorage.setItem('nexus_invoices', JSON.stringify(invoices));
  }, [invoices]);

  useEffect(() => {
    localStorage.setItem('nexus_reconciliations', JSON.stringify(reconciliations));
  }, [reconciliations]);

  // Adjust routing tabs for selected role visibility
  const getTabsForRole = () => {
    switch (activeRole) {
      case 'PUBLIC_VISITOR':
        return ['PRICING_TIERS', 'PRIVACY_TERMS', 'COMMUNITY_DISCLOSURE'];
      case 'CLIENT':
        return ['METERING_MAPPING', 'CLIENT_INVOICES', 'SEARCH_CREDITS_STATEMENT'];
      case 'ADMIN':
        return ['METERING', 'TAX_COMPLIANCE_HUB', 'INVOICES', 'RECONCILIATION', 'EXPORTS', 'COMMUNITY', 'VAT_MONITOR', 'ACCOUNTING_LEDGER'];
      case 'SUPER_ADMIN':
      default:
        return [
          'METERING', 'TAX_COMPLIANCE_HUB', 'ACCOUNTING_LEDGER', 'INVOICES', 'RECONCILIATION', 
          'VAT_MONITOR', 'COMMUNITY', 'EXPORTS', 'MTD_GATEWAY', 'RETENTION_REVIEWS'
        ];
    }
  };

  // Adjust active tab when role yields fewer tabs
  useEffect(() => {
    const defaultTabs = getTabsForRole();
    if (!defaultTabs.includes(activeTab)) {
      setActiveTab(defaultTabs[0]);
    }
  }, [activeRole]);

  // Calculations for total quantities
  const totalEligibleGross = reconciliations
    .filter(r => r.status === 'Matched' || r.status === 'Refunded')
    .reduce((sum, r) => sum + r.gross_amount, 0);

  const totalRefundsValue = reconciliations
    .filter(r => r.status === 'Refunded')
    .reduce((sum, r) => sum + r.gross_amount, 0);

  const totalChargebacksValue = reconciliations
    .filter(r => r.status === 'Chargeback')
    .reduce((sum, r) => sum + r.gross_amount, 0);

  const netReceiptsForCommunity = totalEligibleGross - totalRefundsValue - totalChargebacksValue;
  // Allocation split: 3% is total
  const communityTotalAllocation = netReceiptsForCommunity * 0.03;
  const charity1Split = communityTotalAllocation / 3;
  const charity2Split = communityTotalAllocation / 3;
  const charity3Split = communityTotalAllocation / 3;

  // Search trigger simulation helper (failed searches do not consume. Cached searches do not consume.)
  const simulateSearchTrigger = (type: 'SUCCESS' | 'CACHED' | 'FAILED') => {
    const rawId = `evt_srch_${Math.floor(Math.random() * 89999 + 10000)}`;
    const workspaceId = "wksp_alpha_991";
    const userId = "usr_mahmood_Director";

    let quantity = 0;
    let price = 0;
    let balanceBefore = searchCreditsBalance;
    let balanceAfter = searchCreditsBalance;
    let isBillable = false;
    let bStatus: 'METERED' | 'EXCLUDED' = 'EXCLUDED';
    let sourceAction = "";
    let eventType: MeteringEvent['event_type'] = 'NEXUS_SEARCH_QUERY';

    if (type === 'SUCCESS') {
      quantity = 1;
      price = 0.01;
      balanceAfter = balanceBefore - 1;
      setSearchCreditsBalance(balanceAfter);
      isBillable = true;
      bStatus = 'METERED';
      sourceAction = `Successful query logged: "${queryPlaceholder()}"`;
      eventType = 'NEXUS_SEARCH_QUERY';
      showToastMsg("Search processed successfully - consumed 1 credit.", "success");
    } else if (type === 'CACHED') {
      quantity = 0;
      price = 0.00;
      isBillable = false;
      bStatus = 'EXCLUDED';
      sourceAction = "Duplicate query served instantly via regional Redis cache edge";
      eventType = 'CACHED_SEARCH';
      showToastMsg("Duplicate hit - served from cache. Cost is £0.00 (exempt).", "info");
    } else {
      quantity = 0;
      price = 0.00;
      isBillable = false;
      bStatus = 'EXCLUDED';
      sourceAction = "Elasticsearch shard failed validation - failed search credited back";
      eventType = 'FAILED_SEARCH';
      showToastMsg("Search query failed server-side - no credit consumed.", "error");
    }

    const newEvent: MeteringEvent = {
      event_id: rawId,
      event_type: eventType,
      workspace_id: workspaceId,
      user_id: userId,
      product_module: "Search Vector Engine",
      quantity,
      unit_price: price,
      currency: "GBP",
      credit_balance_before: balanceBefore,
      credit_balance_after: balanceAfter,
      billable_true_false: isBillable,
      billing_status: bStatus,
      source_action: sourceAction,
      created_at_utc: new Date().toISOString().replace('T', ' ').substring(0, 19) + 'Z',
      approval_required: false,
      approval_status: 'AUTO_APPROVED'
    };

    setMeteringEvents(prev => [newEvent, ...prev]);

    // Create a transaction proxy in Accounting Layer as well if successful to simulate Layer 2 integration
    if (type === 'SUCCESS') {
      const journalId = `jn_srch_${Math.floor(Math.random() * 899 + 100)}`;
      const newJournal: AccountingJournal = {
        journal_id: journalId,
        transaction_id: `txn_cr_${rawId}`,
        invoice_id: "Internal Sweep API",
        debit_account: "Deferred Revenue / Unused Credits Liability",
        credit_account: "Search Credit Revenue",
        amount: 0.01,
        currency: "GBP",
        VAT_status: vatRegistered ? 'VAT_STANDARD_20' : 'VAT_NOT_REGISTERED',
        VAT_rate: vatRegistered ? 20 : 0,
        VAT_amount: vatRegistered ? 0.002 : 0,
        payment_provider_reference: "INTERNAL_METERING_SWEEP",
        created_at_utc: newEvent.created_at_utc,
        locked_status: true,
        correction_status: 'ORIGINAL',
        audit_hash: btoa(journalId).substring(0, 15),
        accountant_review_status: 'UNREVIEWED'
      };
      setAccountingJournals(prev => [newJournal, ...prev]);
    }
  };

  const queryPlaceholder = () => {
    const queries = [
      "NexoraX billing records grant billing details",
      "Accountant review package release",
      "Dunfermline digital transformation pitch",
      "B2B SaaS metric audit report",
      "MTD future roadmap outline",
      "Secure payment token check"
    ];
    return queries[Math.floor(Math.random() * queries.length)];
  };

  // Manual accounting adjustment helper
  const submitManualAdjustment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newJournalDebitAccount || !newJournalCreditAccount || newJournalAmount <= 0) {
      showToastMsg("Please provide all debit/credit fields and active amount.", "error");
      return;
    }

    const journalId = `jn_adj_${Math.floor(Math.random() * 8999 + 1000)}`;
    const newJournal: AccountingJournal = {
      journal_id: journalId,
      transaction_id: `txn_adj_${Math.floor(Math.random() * 89999 + 10000)}`,
      invoice_id: "MANUAL_ADJUSTMENT",
      debit_account: newJournalDebitAccount,
      credit_account: newJournalCreditAccount,
      amount: newJournalAmount,
      currency: "GBP",
      VAT_status: 'VAT_NOT_REGISTERED',
      VAT_rate: 0,
      VAT_amount: 0.00,
      payment_provider_reference: "Internal Manual Adjustment Ledger",
      created_at_utc: new Date().toISOString().replace('T', ' ').substring(0, 19) + 'Z',
      locked_status: false,
      correction_status: 'ADJUSTMENT',
      audit_hash: btoa(journalId + newJournalAmount).substring(0, 16),
      accountant_review_status: 'UNREVIEWED'
    };

    setAccountingJournals(prev => [newJournal, ...prev]);
    setNewJournalDebitAccount('');
    setNewJournalCreditAccount('');
    setNewJournalAmount(0);
    setShowAdjustmentForm(false);
    showToastMsg("Manual journal entry staged. Super Admin authorization recorded.", "success");
  };

  // Locked journal toggle or Accountant Review status edit
  const toggleReviewStatus = (id: string, status: AccountingJournal['accountant_review_status']) => {
    setAccountingJournals(prev => {
      return prev.map(j => {
        if (j.journal_id === id) {
          return { ...j, accountant_review_status: status };
        }
        return j;
      });
    });
    showToastMsg(`Review status updated to ${status} for ${id}`, "info");
  };

  // Download simulation trigger
  const triggerMockExport = (format: string, type: string) => {
    showToastMsg(`Generating ${format} export for ${type} recordsets...`, "info");
    setTimeout(() => {
      showToastMsg(`Export file ready. Downloaded ${type}.${format.toLowerCase()}`, "success");
    }, 1200);
  };

  // VAT Monitor progress bar helpers
  const getThresholdColor = (turnover: number) => {
    const thresholdVal = 85000;
    const pct = (turnover / thresholdVal) * 100;
    if (pct >= 95) return 'bg-rose-500 shadow-[0_0_12px_rgba(239,68,68,0.5)]';
    if (pct >= 85) return 'bg-amber-500 shadow-[0_0_12px_rgba(245,158,11,0.5)]';
    if (pct >= 70) return 'bg-yellow-400 shadow-[0_0_12px_rgba(250,204,21,0.5)]';
    return 'bg-cyan-400 shadow-[0_0_12px_rgba(6,182,212,0.5)]';
  };

  const getVATWarningLevel = (turnover: number) => {
    const thresholdVal = 85000;
    const pct = (turnover / thresholdVal) * 100;
    if (pct >= 100) return 'EXCEEDED';
    if (pct >= 95) return '95_RISK';
    if (pct >= 85) return '85_WARNING';
    if (pct >= 70) return '70_NOTICE';
    return 'SAFE';
  };

  return (
    <div id="nexus-accounting-system" className="min-h-screen text-slate-100 flex flex-col space-y-6 select-none relative font-sans">
      
      {/* Absolute top dashboard details strip */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-purple-950/20 backdrop-blur-md p-4 md:px-6 rounded-2xl border border-white/5 shadow-[0_4px_30px_rgba(0,0,0,0.4)]">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse"></span>
            <span className="text-xs font-mono font-extrabold uppercase tracking-wider text-cyan-400">System Ready for Public Preview</span>
          </div>
          <p className="text-[11px] text-slate-400 font-mono tracking-wide">
            Nexus Accounting & Metering System is built for internal accounting-readiness and accountant export. 
            <span className="text-amber-400 font-semibold ml-1">Professional review is required before live VAT filing, legal reliance, or final company accounts.</span>
          </p>
        </div>

        {/* Perspective toggle (Public, Client, Admin, Super Admin) */}
        <div className="flex bg-black/40 border border-zinc-800 p-1.5 rounded-xl gap-1 shrink-0">
          <span className="text-[9px] font-mono uppercase text-zinc-500 px-2 py-1 select-none self-center font-bold">Role View:</span>
          {(['PUBLIC_VISITOR', 'CLIENT', 'ADMIN', 'SUPER_ADMIN'] as const).map((r) => (
            <button
              key={r}
              onClick={() => {
                setActiveRole(r);
                showToastMsg(`Role view switched to ${r}`, "info");
              }}
              className={`px-2.5 py-1 text-[8.5px] font-mono uppercase font-black tracking-wider rounded-lg border transition-all cursor-pointer ${
                activeRole === r 
                  ? 'bg-purple-500/15 border-purple-500 text-[#e840f3] shadow-[0_0_10px_rgba(232,64,243,0.3)]'
                  : 'bg-transparent border-transparent text-slate-500 hover:text-slate-350'
              }`}
            >
              {r.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Main Container Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left column: Subtab navigation menu & summary metrics */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-black/60 backdrop-blur-md border border-white/10 p-5 rounded-2xl space-y-6 shadow-xl relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#e840f3]/5 rounded-full blur-3xl pointer-events-none"></div>
            
            <div className="space-y-1">
              <h2 className="text-sm font-black font-sans uppercase tracking-widest text-[#00ffd2]">
                Nexus Workspace Controls
              </h2>
              <span className="text-[9px] font-mono text-zinc-500 uppercase block tracking-widest">
                Accounting & Metering Hub
              </span>
            </div>

            {/* Render items based on role list */}
            <div className="flex flex-col gap-2">
              {getTabsForRole().map((t) => {
                const labelMap: Record<string, string> = {
                  'METERING': '🌌 Product Metering Ledger',
                  'TAX_COMPLIANCE_HUB': '⚖️ Tax & Compliance Hub',
                  'ACCOUNTING_LEDGER': '📋 General Accounting Ledger',
                  'INVOICES': '✉️ Invoices & Receipts',
                  'RECONCILIATION': '🤝 Payment Reconciliation',
                  'VAT_MONITOR': '📊 VAT Turnover Monitor',
                  'COMMUNITY': '🌱 Community Allocation',
                  'EXPORTS': '📤 QuickBooks/Xero Export',
                  'MTD_GATEWAY': '💻 MTD HMRC Gateway',
                  'RETENTION_REVIEWS': '📂 Record Retention Policy',
                  'PRICING_TIERS': '🏷️ SaaS Pricing Plans',
                  'PRIVACY_TERMS': '🔒 Privacy & Terms',
                  'COMMUNITY_DISCLOSURE': '📢 Public Community Split',
                  'CLIENT_INVOICES': '📑 My Paid Invoices',
                  'SEARCH_CREDITS_STATEMENT': '🔑 Search Credits Ledger',
                  'METERING_MAPPING': '⚙️ Ecosystem Consumption'
                };
                return (
                  <button
                    key={t}
                    onClick={() => setActiveTab(t)}
                    className={`w-full text-left px-3 py-3 rounded-xl border text-[10px] font-sans font-bold uppercase tracking-wider transition-all cursor-pointer flex items-center justify-between ${
                      activeTab === t 
                        ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400 shadow-[0_0_12px_rgba(6,182,212,0.1)]'
                        : 'bg-zinc-950/40 border-zinc-900 text-slate-400 hover:text-white hover:bg-zinc-900/40'
                    }`}
                  >
                    <span>{labelMap[t] || t}</span>
                    <ChevronRight size={10} className={activeTab === t ? 'text-cyan-400 animate-pulse' : 'text-slate-600'} />
                  </button>
                );
              })}
            </div>

            <hr className="border-white/5" />

            <div className="space-y-3 font-mono text-[9px] text-zinc-400">
              <div className="flex justify-between">
                <span>Core Currency:</span>
                <span className="text-white font-extrabold font-mono">GBP (£)</span>
              </div>
              <div className="flex justify-between">
                <span>Secure Provider:</span>
                <span className="text-[#00ffd2] font-mono font-extrabold">[ Stripe Secure API ]</span>
              </div>
              <div className="flex justify-between">
                <span>Auto-Reconcile:</span>
                <span className="text-emerald-400 font-mono font-extrabold flex items-center gap-0.5">
                  <CheckCircle2 size={9} /> ENABLED
                </span>
              </div>
            </div>
          </div>

          {/* Quick Metrics card if Admin view is enabled */}
          {(activeRole === 'ADMIN' || activeRole === 'SUPER_ADMIN') && (
            <div className="bg-[#02050a]/80 backdrop-blur-md border border-white/5 p-5 rounded-2xl space-y-4 shadow-xl">
              <h3 className="text-[10px] font-sans uppercase font-black text-slate-400 tracking-wider">
                Ledger Status Flags
              </h3>
              <div className="space-y-3 font-mono">
                <div className="p-2.5 rounded-xl bg-zinc-950/80 border border-zinc-900 text-left">
                  <span className="text-zinc-500 uppercase block text-[8px]">Reconciled Revenue</span>
                  <strong className="text-xs text-white block mt-0.5">£{totalEligibleGross.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</strong>
                </div>
                <div className="p-2.5 rounded-xl bg-zinc-950/80 border border-zinc-900 text-left">
                  <span className="text-zinc-500 uppercase block text-[8px]">Community Fund Allocation</span>
                  <strong className="text-xs text-[#e840f3] block mt-0.5">£{communityTotalAllocation.toFixed(2)}</strong>
                </div>
                {vatRegistered ? (
                  <div className="p-2.5 rounded-xl bg-emerald-950/30 border border-emerald-500/20 text-left">
                    <span className="text-emerald-400 uppercase block text-[8px]">VAT Active</span>
                    <strong className="text-xs text-emerald-400 block mt-0.5">{vatNumber}</strong>
                  </div>
                ) : (
                  <div className="p-2.5 rounded-xl bg-zinc-950/80 border border-zinc-900 text-left">
                    <span className="text-zinc-650 uppercase block text-[8px]">VAT Status</span>
                    <strong className="text-xs text-slate-500 block mt-0.5">NOT REGISTERED</strong>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right column: Dynamic Display Panel */}
        <div className="lg:col-span-9 space-y-6">
          <AnimatePresence mode="wait">
            
            {/* TAB: METERING - Layer 1 */}
            {activeTab === 'METERING' && (
              <motion.div
                key="tab-metering"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-6"
              >
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
                  <div>
                    <h3 className="text-lg font-black font-sans uppercase tracking-tight flex items-center gap-2">
                      <Terminal size={18} className="text-cyan-400 animate-pulse" />
                      Nexus Metering Ledger (Layer 1)
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">
                      Continuous real-time product use evaluation. Failed or cached searches consume 0 credits.
                    </p>
                  </div>
                  
                  {/* Simulation Controllers */}
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => simulateSearchTrigger('SUCCESS')}
                      className="px-2.5 py-1.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-xl text-[9px] font-mono font-bold text-white uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1"
                    >
                      <Plus size={10} className="text-cyan-400" /> + Query
                    </button>
                    <button
                      onClick={() => simulateSearchTrigger('CACHED')}
                      className="px-2.5 py-1.5 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/30 rounded-xl text-[9px] font-mono font-bold text-yellow-400 uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1"
                    >
                      <History size={10} className="text-yellow-400" /> + Cache Hit
                    </button>
                    <button
                      onClick={() => simulateSearchTrigger('FAILED')}
                      className="px-2.5 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 rounded-xl text-[9px] font-mono font-bold text-rose-400 uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1"
                    >
                      <Ban size={10} className="text-rose-400" /> + Fails Unit
                    </button>
                  </div>
                </div>

                {/* Balance Statement summary in obsidian block layout */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-[#02050b] p-4 border border-zinc-900 rounded-2xl">
                    <span className="text-slate-500 uppercase block font-bold text-[8.5px] font-sans">Active Search Balance</span>
                    <strong className="text-xl text-cyan-450 block mt-1">{searchCreditsBalance} Credits</strong>
                    <span className="text-slate-650 block text-[8px] font-mono mt-0.5">Workspace Alpha-991 Statement</span>
                  </div>
                  <div className="bg-[#02050b] p-4 border border-zinc-900 rounded-2xl">
                    <span className="text-slate-550 uppercase block font-semibold text-[8.5px] font-sans">Total Metered Units Logged</span>
                    <strong className="text-xl text-yellow-400 block mt-1">{meteringEvents.length} transactions</strong>
                    <span className="text-slate-650 block text-[8px] font-mono mt-0.5">Audited server-side telemetry</span>
                  </div>
                  <div className="bg-[#02050b] p-4 border border-zinc-900 rounded-2xl">
                    <span className="text-slate-550 uppercase block font-black text-[8.5px] font-sans">Internal Unit Cost</span>
                    <strong className="text-xl text-[#e840f3] block mt-1">£0.01 per valid hit</strong>
                    <span className="text-slate-650 block text-[8px] font-mono mt-0.5">Maintenance & Hosting SLA Rate</span>
                  </div>
                </div>

                {/* Grid records filter information */}
                <div className="border border-white/5 rounded-2xl overflow-hidden bg-zinc-950/50">
                  <div className="bg-black/80 px-4 py-3 border-b border-white/5 flex justify-between items-center text-[10px] text-slate-500">
                    <span className="font-mono uppercase font-black tracking-wider text-slate-350">[ Live Metering Events Table - Server authoritative ]</span>
                    <span className="bg-cyan-950/40 border border-cyan-800 text-cyan-400 px-2 py-0.5 rounded font-mono text-[8px] tracking-widest font-black">ACTIVE TELEMETRY</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left font-mono text-[10px] leading-relaxed border-collapse">
                      <thead>
                        <tr className="border-b border-zinc-900 bg-white/2">
                          <th className="p-3.5 text-zinc-400">Event ID / Created</th>
                          <th className="p-3.5 text-zinc-400">Type</th>
                          <th className="p-3.5 text-zinc-400">Workspace / Source</th>
                          <th className="p-3.5 text-zinc-400 text-right">Units</th>
                          <th className="p-3.5 text-zinc-400 text-right">Rate</th>
                          <th className="p-3.5 text-zinc-400 text-right">Balance After</th>
                          <th className="p-3.5 text-zinc-400">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-900">
                        {meteringEvents.map((e) => (
                          <tr key={e.event_id} className="hover:bg-white/2 transition-colors">
                            <td className="p-3.5 max-w-[120px] truncate">
                              <span className="text-[#00ffd2] block font-bold">{e.event_id}</span>
                              <span className="text-zinc-600 block text-[8px]">{e.created_at_utc}</span>
                            </td>
                            <td className="p-3.5">
                              <span className={`px-1.5 py-0.5 rounded text-[8px] font-extrabold ${
                                e.event_type === 'NEXUS_SEARCH_QUERY' ? 'bg-cyan-950/60 border border-cyan-500/30 text-cyan-400' :
                                e.event_type === 'CACHED_SEARCH' ? 'bg-yellow-950/60 border border-yellow-500/30 text-yellow-400' :
                                e.event_type === 'FAILED_SEARCH' ? 'bg-rose-950/60 border border-rose-500/30 text-rose-400' :
                                'bg-purple-950/60 border border-purple-500/30 text-[#e840f3]'
                              }`}>
                                {e.event_type}
                              </span>
                            </td>
                            <td className="p-3.5">
                              <span className="text-zinc-300 block">{e.workspace_id}</span>
                              <span className="text-zinc-500 block text-[8px] truncate max-w-[200px]">{e.source_action}</span>
                            </td>
                            <td className="p-3.5 text-right font-bold text-white">{e.quantity}</td>
                            <td className="p-3.5 text-right font-black text-slate-400">£{e.unit_price.toFixed(2)}</td>
                            <td className="p-3.5 text-right text-cyan-300 font-bold">{e.credit_balance_after} cr</td>
                            <td className="p-3.5">
                              <span className={`px-1.5 py-0.5 rounded text-[8px] font-black tracking-wider ${
                                e.billing_status === 'METERED' ? 'bg-emerald-950/40 text-emerald-400' :
                                e.billing_status === 'EXCLUDED' ? 'bg-slate-900 text-slate-500' :
                                'bg-amber-950/45 text-amber-400'
                              }`}>
                                {e.billing_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {/* TAB: GENERAL ACCOUNTING LEDGER - Layer 2 */}
            {activeTab === 'ACCOUNTING_LEDGER' && (
              <motion.div
                key="tab-general-accounting"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-6"
              >
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
                  <div>
                    <h3 className="text-lg font-black font-sans uppercase tracking-tight flex items-center gap-2">
                      <Scale size={18} className="text-[#e840f3]" />
                      Nexus Accounting Ledger (Layer 2)
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">
                      Convert usage metrics and payment events into accounting-ready general journals.
                    </p>
                  </div>

                  {activeRole === 'SUPER_ADMIN' && (
                    <button
                      onClick={() => setShowAdjustmentForm(!showAdjustmentForm)}
                      className="px-3 py-1.5 bg-[#e840f3]/10 hover:bg-[#e840f3]/25 border border-[#e840f3]/30 rounded-xl text-[9px] font-mono font-bold text-white uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1.5"
                    >
                      <Plus size={11} className="text-[#e840f3]" /> Create Adjustment Journal
                    </button>
                  )}
                </div>

                {/* Balance validation alert - Debits and Credits MUST balance! */}
                <div className="p-4 rounded-xl bg-cyan-950/30 border border-cyan-500/20 text-xs text-left leading-relaxed font-mono flex items-start gap-2 text-cyan-300">
                  <Info size={16} className="text-cyan-400 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-white block font-bold">Ledger Balance Principle (Balanced Debits & Credits)</strong>
                    All general journals are written in immutable double-entry ledger blocks. Total debits must equal total credits at accounting sweep points. Once records are finalized, they are permanently locked to preserve accounting readiness.
                  </div>
                </div>

                {/* Staging Adjustment Form */}
                {showAdjustmentForm && (
                  <motion.form
                    onSubmit={submitManualAdjustment}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-5 bg-zinc-950/80 border border-purple-500/20 rounded-2xl space-y-4 text-left"
                  >
                    <h4 className="text-xs font-mono font-black text-amber-400 uppercase tracking-widest border-b border-zinc-900 pb-2">
                      🔐 Super Admin - Stage Manual General Ledger adjustment
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono text-xs">
                      <div>
                        <label className="text-slate-400 block mb-1">Debit Account:</label>
                        <input
                          type="text"
                          placeholder="e.g. Accounts Receivable"
                          value={newJournalDebitAccount}
                          onChange={(e) => setNewJournalDebitAccount(e.target.value)}
                          className="w-full bg-[#02050b] border border-zinc-800 rounded p-2 text-white font-mono focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-slate-400 block mb-1">Credit Account:</label>
                        <input
                          type="text"
                          placeholder="e.g. Sales Revenue"
                          value={newJournalCreditAccount}
                          onChange={(e) => setNewJournalCreditAccount(e.target.value)}
                          className="w-full bg-[#02050b] border border-zinc-800 rounded p-2 text-white font-mono focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-slate-400 block mb-1">Amount (GBP):</label>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={newJournalAmount || ''}
                          onChange={(e) => setNewJournalAmount(parseFloat(e.target.value))}
                          className="w-full bg-[#02050b] border border-zinc-800 rounded p-2 text-white font-mono focus:border-cyan-500 focus:outline-none animate-pulse"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => setShowAdjustmentForm(false)}
                        className="px-3 py-1 bg-zinc-900 border border-zinc-800 rounded hover:bg-zinc-850 cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-1 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded font-bold text-cyan-400 cursor-pointer"
                      >
                        Commit Staged Adjustment
                      </button>
                    </div>
                  </motion.form>
                )}

                {/* Ledger entries list */}
                <div className="border border-white/5 rounded-2xl overflow-hidden bg-zinc-950/50">
                  <div className="bg-black/80 px-4 py-3 border-b border-white/5 flex justify-between items-center text-[10px] text-zinc-500">
                    <span className="font-mono uppercase font-black tracking-wider text-zinc-350">[ Double-Entry Immutable General Journals ]</span>
                    <span className="text-[#e840f3] font-mono text-[8.5px] font-black">AUDITED</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left font-mono text-[10px] leading-relaxed border-collapse">
                      <thead>
                        <tr className="border-b border-zinc-900 bg-white/2">
                          <th className="p-3.5 text-zinc-400">Journal ID / Dated</th>
                          <th className="p-3.5 text-zinc-400">Accounts (Debit / Credit)</th>
                          <th className="p-3.5 text-zinc-400 text-right">Amount</th>
                          <th className="p-3.5 text-zinc-400">VAT Code</th>
                          <th className="p-3.5 text-zinc-400">Audit Handshake Hash</th>
                          <th className="p-3.5 text-zinc-400">Status</th>
                          <th className="p-3.5 text-zinc-400 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-900">
                        {accountingJournals.map((j) => (
                          <tr key={j.journal_id} className="hover:bg-white/2 transition-colors">
                            <td className="p-3.5">
                              <span className="text-white block font-bold">{j.journal_id}</span>
                              <span className="text-zinc-650 block text-[8px]">{j.created_at_utc}</span>
                            </td>
                            <td className="p-3.5">
                              <div className="flex items-center gap-1.5 text-emerald-400 font-bold">
                                <span>Dr: {j.debit_account}</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-slate-500">
                                <span>Cr: {j.credit_account}</span>
                              </div>
                            </td>
                            <td className="p-3.5 text-right font-black text-white">
                              £{j.amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="p-3.5">
                              <span className={`px-1.5 py-0.5 rounded text-[8px] font-extrabold ${
                                j.VAT_status === 'VAT_STANDARD_20' ? 'bg-cyan-950/60 border border-cyan-500/30 text-cyan-400' : 'bg-slate-900 text-slate-500'
                              }`}>
                                {j.VAT_status === 'VAT_STANDARD_20' ? 'S (20%)' : 'N/A'}
                              </span>
                            </td>
                            <td className="p-3.5">
                              <span className="text-zinc-400 text-xs block truncate max-w-[130px]" title={j.audit_hash}>
                                sha256_{j.audit_hash}
                              </span>
                            </td>
                            <td className="p-3.5">
                              <div className="flex flex-col gap-1">
                                <span className="text-[8px] font-black text-slate-550 block">LOCK: {j.locked_status ? '🔐 LOCKED' : '🔓 OPEN'}</span>
                                <span className={`text-[8px] font-black block ${
                                  j.accountant_review_status === 'APPROVED' ? 'text-emerald-400' : 'text-yellow-500'
                                }`}>
                                  {j.accountant_review_status}
                                </span>
                              </div>
                            </td>
                            <td className="p-3.5 text-center">
                              <div className="flex justify-center gap-1.5">
                                <button
                                  onClick={() => toggleReviewStatus(j.journal_id, 'APPROVED')}
                                  title="Approve Journal"
                                  className="p-1 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 cursor-pointer"
                                >
                                  <CheckCircle size={10} />
                                </button>
                                <button
                                  onClick={() => toggleReviewStatus(j.journal_id, 'DISPUTED')}
                                  title="Flag Disputed"
                                  className="p-1 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 cursor-pointer"
                                >
                                  <Ban size={10} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {/* TAB: INVOICES REGISTER */}
            {activeTab === 'INVOICES' && (
              <motion.div
                key="tab-invoices"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-6"
              >
                <div className="flex justify-between items-center border-b border-white/5 pb-4">
                  <div>
                    <h3 className="text-lg font-black font-sans uppercase tracking-tight flex items-center gap-2">
                      <FileText size={18} className="text-[#00ffd2]" />
                      Secure Invoice Register
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">
                      Issued records cannot be deleted. Sequential internal receipt tracking.
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto border border-white/5 rounded-2xl bg-zinc-950/50">
                  <table className="w-full text-left font-mono text-[10px] leading-relaxed border-collapse">
                    <thead>
                      <tr className="border-b border-zinc-900 bg-white/2">
                        <th className="p-3.5 text-zinc-400">Invoice Number</th>
                        <th className="p-3.5 text-zinc-400">Customer & Company</th>
                        <th className="p-3.5 text-zinc-400">Period</th>
                        <th className="p-3.5 text-zinc-400 text-right">Base Price</th>
                        <th className="p-3.5 text-zinc-400 text-right">Maintenance</th>
                        <th className="p-3.5 text-zinc-400 text-right">VAT</th>
                        <th className="p-3.5 text-zinc-400 text-right">Grand Total</th>
                        <th className="p-3.5 text-zinc-400">Payment Status</th>
                        <th className="p-3.5 text-zinc-400 text-center">Receipt Link</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-900">
                      {invoices.map((inv) => (
                        <tr key={inv.invoice_number} className="hover:bg-white/2 transition-colors">
                          <td className="p-3.5 font-bold text-white">
                            <span>{inv.invoice_number}</span>
                            <span className="text-zinc-650 block text-[8px]">{inv.issue_date}</span>
                          </td>
                          <td className="p-3.5">
                            <span className="text-[#00ffd2] block font-extrabold">{inv.customer_name}</span>
                            <span className="text-zinc-400 block text-[8px]">{inv.company_name}</span>
                          </td>
                          <td className="p-3.5 text-slate-350">{inv.billing_period}</td>
                          <td className="p-3.5 text-right font-semibold">£{inv.base_price.toFixed(2)}</td>
                          <td className="p-3.5 text-right text-zinc-500">£{inv.maintenance_hosting_fee.toFixed(2)}</td>
                          <td className="p-3.5 text-right font-black text-cyan-300">
                            {vatRegistered ? `£${(inv.base_price * (customVATRate/100)).toFixed(2)}` : '£0.00'}
                          </td>
                          <td className="p-3.5 text-right font-black text-white">
                            £{(inv.base_price + inv.maintenance_hosting_fee + (vatRegistered ? (inv.base_price * (customVATRate/100)) : 0)).toFixed(2)}
                          </td>
                          <td className="p-3.5">
                            <span className={`px-2 py-0.5 rounded text-[8px] font-black tracking-widest ${
                              inv.payment_status === 'PAID' ? 'bg-emerald-950/60 border border-emerald-500/20 text-emerald-400' : 'bg-rose-950/60 border border-rose-500/20 text-rose-450'
                            }`}>
                              {inv.payment_status}
                            </span>
                          </td>
                          <td className="p-3.5 text-center">
                            {inv.receipt_link ? (
                              <a
                                href={inv.receipt_link}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-500/10 hover:bg-cyan-500/25 border border-cyan-500/30 rounded text-cyan-400 text-[8px] font-bold uppercase transition"
                              >
                                View Stripe Receipt <ArrowRight size={8} />
                              </a>
                            ) : (
                              <span className="text-slate-600 text-[8px]">Pending pay</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* TAB: PAYMENT RECONCILIATION */}
            {activeTab === 'RECONCILIATION' && (
              <motion.div
                key="tab-reconciliation"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-6"
              >
                <div className="flex justify-between items-center border-b border-white/5 pb-4">
                  <div>
                    <h3 className="text-lg font-black font-sans uppercase tracking-tight flex items-center gap-2">
                      <ShieldCheck size={18} className="text-emerald-400" />
                      Payment reconciliation Matrix
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">
                      Service activation triggers strictly from verified gateway states only.
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto border border-white/5 rounded-2xl bg-zinc-950/50 font-mono text-[10px]">
                  <table className="w-full text-left leading-relaxed border-collapse">
                    <thead>
                      <tr className="border-b border-zinc-900 bg-white/2">
                        <th className="p-3.5 text-zinc-400">Transaction ID</th>
                        <th className="p-3.5 text-zinc-400">Payment Provider Ref</th>
                        <th className="p-3.5 text-zinc-440 text-right">Gross Amount</th>
                        <th className="p-3.5 text-zinc-400 text-right">Processor Fee</th>
                        <th className="p-3.5 text-zinc-400 text-right">Net Received</th>
                        <th className="p-3.5 text-zinc-400 text-center">Invoice ID Match</th>
                        <th className="p-3.5 text-zinc-400">Status</th>
                        <th className="p-3.5 text-zinc-400">Reconcile Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-900">
                      {reconciliations.map((rec) => (
                        <tr key={rec.payment_id} className="hover:bg-white/2 transition-colors">
                          <td className="p-3.5 font-bold text-white">{rec.payment_id}</td>
                          <td className="p-3.5 font-semibold text-[#00ffd2]">{rec.provider_reference || 'PENDING RECORD'}</td>
                          <td className="p-3.5 text-right font-bold text-white">£{rec.gross_amount.toFixed(2)}</td>
                          <td className="p-3.5 text-right text-rose-400">-£{rec.fee_amount.toFixed(2)}</td>
                          <td className="p-3.5 text-right text-emerald-400 font-bold">£{rec.net_received.toFixed(2)}</td>
                          <td className="p-3.5 text-center font-bold text-zinc-400">
                            {rec.invoice_id}
                          </td>
                          <td className="p-3.5">
                            <span className={`px-2 py-0.5 rounded text-[8px] font-black tracking-widest ${
                              rec.status === 'Matched' ? 'bg-emerald-950/50 text-emerald-400' :
                              rec.status === 'Mismatch' ? 'bg-amber-950/50 text-amber-500' :
                              rec.status === 'Refunded' ? 'bg-rose-950/50 text-rose-400' :
                              'bg-zinc-900 text-zinc-400'
                            }`}>
                              {rec.status}
                            </span>
                            {rec.mismatch_warning && (
                              <span className="block text-[7px] text-red-400 mt-1 uppercase max-w-[150px] leading-tight">
                                ⚠️ {rec.mismatch_warning}
                              </span>
                            )}
                          </td>
                          <td className="p-3.5">
                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                              rec.reconciliation_status === 'RECONCILED' ? 'text-emerald-400' : 'text-amber-500'
                            }`}>
                              {rec.reconciliation_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* TAB: VAT MONITOR */}
            {activeTab === 'VAT_MONITOR' && (
              <motion.div
                key="tab-vat-monitor"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-6"
              >
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
                  <div>
                    <h3 className="text-lg font-black font-sans uppercase tracking-tight flex items-center gap-2">
                      <TrendingUp size={18} className="text-[#00e5ff]" />
                      Rolling VAT Turnover Monitor
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">
                      Pre-emptive statutory threshold diagnostics.
                    </p>
                  </div>
                  
                  {/* Toggle VAT registration status */}
                  <div className="flex bg-black/40 border border-zinc-805 p-1 rounded-xl">
                    <button
                      onClick={() => {
                        setVatRegistered(true);
                        showToastMsg("VAT registration active. Standard VAT standard rate applied.", "info");
                      }}
                      className={`px-3 py-1.5 text-[9px] font-mono font-bold uppercase rounded-lg transition-all cursor-pointer ${
                        vatRegistered ? 'bg-cyan-500/15 border border-cyan-500/20 text-cyan-400' : 'text-slate-500 hover:text-slate-350'
                      }`}
                    >
                      Registered
                    </button>
                    <button
                      onClick={() => {
                        setVatRegistered(false);
                        showToastMsg("VAT disabled. Standard 0 rate applied.", "info");
                      }}
                      className={`px-3 py-1.5 text-[9px] font-mono font-bold uppercase rounded-lg transition-all cursor-pointer ${
                        !vatRegistered ? 'bg-zinc-800 border border-zinc-700 text-zinc-400' : 'text-slate-500 hover:text-slate-305'
                      }`}
                    >
                      Not Registered
                    </button>
                  </div>
                </div>

                {/* Registration Details Block */}
                {vatRegistered ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-[#050c18] border border-cyan-500/20 p-5 rounded-2xl text-left font-mono text-[10px]">
                    <div>
                      <span className="text-zinc-500 block uppercase font-bold text-[8px]">VAT Number</span>
                      <strong className="text-sm font-extrabold text-white">{vatNumber}</strong>
                    </div>
                    <div>
                      <span className="text-zinc-500 block uppercase font-bold text-[8px]">Effective Date</span>
                      <strong className="text-sm font-extrabold text-white">{vatEffectiveDate}</strong>
                    </div>
                    <div>
                      <span className="text-zinc-500 block uppercase font-bold text-[8px]">VAT Wording</span>
                      <strong className="text-sm font-extrabold text-cyan-400">VAT is calculated at checkout where applicable.</strong>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-zinc-950/80 border border-zinc-900 text-left font-mono text-[10px] text-zinc-450 leading-relaxed">
                    ℹ️ <strong className="text-slate-400 font-bold">VAT not charged — NexoraX is not currently VAT-registered.</strong>
                  </div>
                )}

                {/* Progressive statutory limits progress bar */}
                <div className="bg-[#02050b] p-5 border border-zinc-900 rounded-2xl text-left space-y-4">
                  <div className="flex justify-between text-xs font-mono">
                    <div>
                      <span className="text-zinc-550 uppercase block text-[8px]">Rolling 12-Month VAT Turnover</span>
                      <strong className="text-base text-zinc-200">£{rolling12mTurnover.toLocaleString('en-GB') || '0'} <span className="text-zinc-500 text-xs">/ £85,000 threshold</span></strong>
                    </div>
                    <div className="text-right">
                      <span className="text-zinc-550 uppercase block text-[8px]">Voluntary Limit Status</span>
                      <span className="text-cyan-400 font-bold">Enabled</span>
                    </div>
                  </div>

                  {/* Progress bar container */}
                  <div className="w-full bg-zinc-950 h-3 rounded-full overflow-hidden border border-zinc-900 relative">
                    <div 
                      className={`h-full rounded-full transition-all duration-1000 ${getThresholdColor(rolling12mTurnover)}`}
                      style={{ width: `${Math.min(100, (rolling12mTurnover / 85000) * 100)}%` }}
                    ></div>
                    {/* Tick mark at 70%, 85%, 95% */}
                    <div className="absolute top-0 bottom-0 left-[70%] border-r border-zinc-700 w-px h-full"></div>
                    <div className="absolute top-0 bottom-0 left-[85%] border-r border-zinc-700 w-px h-full"></div>
                    <div className="absolute top-0 bottom-0 left-[95%] border-r border-zinc-700 w-px h-full text-[6px] font-bold px-0.5 text-zinc-550"></div>
                  </div>

                  {/* Alert panel and diagnostic warning */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-[9px] font-mono mt-2">
                    <div className={`p-2.5 rounded-lg border ${rolling12mTurnover >= (85000 * 0.7) ? 'border-yellow-450/30 bg-yellow-950/20 text-yellow-400' : 'border-zinc-900 bg-zinc-950/40 text-zinc-650'}`}>
                      <strong>70% Warning threshold</strong>
                      <span className="block mt-0.5">Statutory notice staved. OK.</span>
                    </div>
                    <div className={`p-2.5 rounded-lg border ${rolling12mTurnover >= (85000 * 0.85) ? 'border-amber-450/30 bg-amber-950/20 text-amber-500' : 'border-zinc-900 bg-zinc-950/40 text-zinc-650'}`}>
                      <strong>85% Warning threshold</strong>
                      <span className="block mt-0.5">Expected sweep limits logged.</span>
                    </div>
                    <div className={`p-2.5 rounded-lg border ${rolling12mTurnover >= (85000 * 0.95) ? 'border-rose-450/30 bg-rose-950/20 text-rose-400' : 'border-zinc-900 bg-zinc-950/40 text-zinc-650'}`}>
                      <strong>95% Warning threshold</strong>
                      <span className="block mt-0.5">Audit-ready alert matrix.</span>
                    </div>
                    <div className={`p-2.5 rounded-lg border ${rolling12mTurnover >= 85000 ? 'border-red-500/30 bg-red-950/20 text-red-400' : 'border-zinc-900 bg-zinc-950/40 text-zinc-650'}`}>
                      <strong>Threshold Exceeded</strong>
                      <span className="block mt-0.5">Mandatory statutory filing required.</span>
                    </div>
                  </div>

                  {/* Threshold trigger response warning */}
                  {rolling12mTurnover >= (85000 * 0.70) && (
                    <div className="p-3 bg-yellow-950/30 border border-yellow-500/20 text-[9px] font-mono rounded-lg text-yellow-400 uppercase tracking-widest font-black leading-relaxed">
                      ⚠️ WARNING: ROLLING TURNOVER TURNS THRESHOLD RISK • ACCOUNTANT REVIEW REQUIRED BEFORE LIVE VAT COLLECTION.
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* TAB: COMMUNITY ALLOCATION */}
            {activeTab === 'COMMUNITY' && (
              <motion.div
                key="tab-community"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-6"
              >
                <div className="flex justify-between items-center border-b border-white/5 pb-4">
                  <div>
                    <h3 className="text-lg font-black font-sans uppercase tracking-tight flex items-center gap-2">
                      <Heart size={18} className="text-[#e840f3] animate-pulse" />
                      Community Allocation Register
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">
                      Corporate split models routed to accredited public causes.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-zinc-400">Review Status:</span>
                    <span className={`px-2.5 py-0.5 rounded font-black font-mono text-[8.5px] uppercase border ${
                      communityReviewStatus === 'Approved' 
                        ? 'bg-emerald-950/45 border-emerald-500/20 text-emerald-400' 
                        : 'bg-rose-950/45 border-rose-500/20 text-rose-450 animate-pulse'
                    }`}>
                      {communityReviewStatus}
                    </span>
                  </div>
                </div>

                {beneficiary1Name && beneficiary2Name && beneficiary3Name && calculationBasis && timePeriod && contributionCap && publicDisclosureEnabled && communityReviewStatus === 'Approved' ? (
                  <>
                    {/* Safe public statement */}
                    <div className="p-4 bg-zinc-950/80 border border-zinc-900 rounded-2xl text-xs font-mono text-left leading-relaxed text-zinc-400 space-y-3">
                      <div className="flex items-center gap-2 text-emerald-400 font-bold">
                        <CheckSquare size={14} />
                        <span className="uppercase tracking-wider">Approved Public Allocation Matrix Active</span>
                      </div>
                      <p>
                        Where enabled and documented, NexoraX may allocate <strong className="text-white">3% of eligible net receipts</strong> to approved community or charitable initiatives, split equally as <strong className="text-[#00ffd2]">1% to each of three nominated causes</strong>.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 text-[10px] text-slate-400 border-t border-zinc-900 font-mono">
                        <div>
                          <span className="text-zinc-500">CALCULATION BASIS:</span> <span className="text-white font-bold">{calculationBasis}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">TIME PERIOD:</span> <span className="text-white font-bold">{timePeriod}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">CONTRIBUTION CAP:</span> <span className="text-white font-bold">{contributionCap}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">VAT EXCLUDED:</span> <span className="text-emerald-450 font-bold">{vatExcluded}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">REFUNDS EXCLUDED:</span> <span className="text-emerald-450 font-bold">{refundsExcluded}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">CHARGEBACKS EXCLUDED:</span> <span className="text-emerald-450 font-bold">{chargebacksExcluded}</span>
                        </div>
                      </div>
                    </div>

                    {/* Core Allocation metrics tables */}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-left font-mono text-[10px]">
                      <div className="bg-[#02050b] p-3.5 border border-zinc-900 rounded-2xl">
                        <span className="text-zinc-550 block text-[8px] uppercase">Calculation Period</span>
                        <strong className="text-sm block text-white mt-1">{timePeriod}</strong>
                      </div>
                      <div className="bg-[#02050b] p-3.5 border border-zinc-900 rounded-2xl">
                        <span className="text-zinc-550 block text-[8px] uppercase">Eligible Net Receipts</span>
                        <strong className="text-sm block text-white mt-1">£{netReceiptsForCommunity.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</strong>
                      </div>
                      <div className="bg-[#02050b] p-3.5 border border-zinc-900 rounded-2xl">
                        <span className="text-zinc-550 block text-[8px] uppercase">Total Split Value (3%)</span>
                        <strong className="text-sm block text-[#e840f3] mt-1">£{communityTotalAllocation.toFixed(2)}</strong>
                      </div>
                      <div className="bg-[#02050b] p-3.5 border border-zinc-900 rounded-2xl">
                        <span className="text-zinc-550 block text-[8px] uppercase">Paid Settlement Voucher</span>
                        <strong className="text-sm block text-emerald-450 mt-1">£{communityPaidAllocation.toFixed(2)}</strong>
                      </div>
                    </div>

                    {/* Charity list block layout */}
                    <div className="space-y-3 text-left font-sans">
                      <h4 className="text-xs uppercase font-mono tracking-widest text-slate-400">[ Beneficiary Distribution Nodes ]</h4>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono text-[10px]">
                        <div className="p-4 rounded-xl border border-[#00ffd2]/10 bg-black/60 space-y-2">
                          <strong className="text-[#00ffd2] block uppercase tracking-wider text-[11px]">Cause 1: {beneficiary1Name}</strong>
                          <p className="text-zinc-450 text-[9px] leading-relaxed">Designated Allocation Node (Split: {beneficiary1Percent})</p>
                          <div className="text-right pt-2 border-t border-zinc-900 font-bold text-[#e840f3]">
                            Allocation: £{charity1Split.toFixed(4)}
                          </div>
                        </div>
                        <div className="p-4 rounded-xl border border-indigo-500/10 bg-black/60 space-y-2">
                          <strong className="text-indigo-400 block uppercase tracking-wider text-[11px]">Cause 2: {beneficiary2Name}</strong>
                          <p className="text-zinc-450 text-[9px] leading-relaxed">Designated Allocation Node (Split: {beneficiary2Percent})</p>
                          <div className="text-right pt-2 border-t border-zinc-900 font-bold text-[#e840f3]">
                            Allocation: £{charity2Split.toFixed(4)}
                          </div>
                        </div>
                        <div className="p-4 rounded-xl border border-purple-500/10 bg-black/60 space-y-2">
                          <strong className="text-[#e840f3] block uppercase tracking-wider text-[11px]">Cause 3: {beneficiary3Name}</strong>
                          <p className="text-zinc-450 text-[9px] leading-relaxed">Designated Allocation Node (Split: {beneficiary3Percent})</p>
                          <div className="text-right pt-2 border-t border-zinc-900 font-bold text-[#e840f3]">
                            Allocation: £{charity3Split.toFixed(4)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    {/* INCOMPLETE STATUS CARD */}
                    <div className="p-5 rounded-2xl bg-amber-950/20 border border-amber-500/20 text-left space-y-3">
                      <div className="flex items-center gap-2 text-amber-500 font-bold">
                        <AlertTriangle className="w-5 h-5 animate-bounce text-amber-400 font-bold" />
                        <span className="text-sm font-black uppercase tracking-wider">Community Allocation Details Required</span>
                      </div>
                      <p className="text-xs text-slate-350 leading-relaxed font-mono">
                        The public Community Allocation statistics are currently disabled. Public disclosures require all Nominated Beneficiary Names, Percentages, and Allocation parameters to be compiled and legally reviewed by Administration before public launch.
                      </p>
                    </div>

                    {/* ONLY VISIBLE TO PUBLIC */}
                    {(activeRole === 'PUBLIC_VISITOR' || activeRole === 'CLIENT') && (
                      <div className="p-4 bg-zinc-900/40 rounded-xl text-[10px] font-mono text-zinc-500 text-center">
                        Community Allocation parameterization is currently pending administrative review. Disclosures will be populated once fully accredited.
                      </div>
                    )}
                  </div>
                )}

                {/* ADMIN CONFIGURATION MODULE PANEL */}
                {(activeRole === 'ADMIN' || activeRole === 'SUPER_ADMIN') && (
                  <div className="p-6 bg-zinc-950/90 border border-zinc-800 rounded-2xl space-y-4 text-left font-mono text-xs">
                    <div className="flex items-center gap-2 border-b border-zinc-800 pb-3">
                      <Settings className="text-[#00ffd2] w-4 h-4" />
                      <strong className="text-xs uppercase text-white tracking-wider font-sans font-black">Admin Allocation Configurator Panel (Core Control)</strong>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Beneficiaries Section */}
                      <div className="space-y-3.5 bg-black/40 p-4 border border-zinc-900 rounded-xl">
                        <strong className="text-[#e840f3] block tracking-wide uppercase text-[10px] font-black">Beneficiary Allocations</strong>
                        
                        <div className="space-y-2">
                          <label className="text-[9px] text-zinc-500 block uppercase font-bold">Beneficiary 1 Name & Split %</label>
                          <div className="flex gap-2">
                            <input 
                              type="text" 
                              placeholder="e.g. Fife Regional Community Initiative" 
                              value={beneficiary1Name} 
                              onChange={(e) => setBeneficiary1Name(e.target.value)} 
                              className="flex-1 bg-black border border-zinc-805 text-zinc-100 rounded px-2.5 py-1 text-[11px] focus:border-cyan-500"
                            />
                            <input 
                              type="text" 
                              value={beneficiary1Percent} 
                              readOnly
                              className="w-12 bg-zinc-900 border border-zinc-800 rounded text-center py-1 text-[11px] text-zinc-500 font-bold"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-[9px] text-zinc-500 block uppercase font-bold">Beneficiary 2 Name & Split %</label>
                          <div className="flex gap-2">
                            <input 
                              type="text" 
                              placeholder="e.g. Global Tech Education resource" 
                              value={beneficiary2Name} 
                              onChange={(e) => setBeneficiary2Name(e.target.value)} 
                              className="flex-1 bg-black border border-zinc-805 text-zinc-100 rounded px-2.5 py-1 text-[11px] focus:border-cyan-500"
                            />
                            <input 
                              type="text" 
                              value={beneficiary2Percent} 
                              readOnly
                              className="w-12 bg-zinc-900 border border-zinc-800 rounded text-center py-1 text-[11px] text-zinc-500 font-bold"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-[9px] text-zinc-500 block uppercase font-bold">Beneficiary 3 Name & Split %</label>
                          <div className="flex gap-2">
                            <input 
                              type="text" 
                              placeholder="e.g. United Kingdom Adaptation Service" 
                              value={beneficiary3Name} 
                              onChange={(e) => setBeneficiary3Name(e.target.value)} 
                              className="flex-1 bg-black border border-zinc-805 text-zinc-100 rounded px-2.5 py-1 text-[11px] focus:border-cyan-500"
                            />
                            <input 
                              type="text" 
                              value={beneficiary3Percent} 
                              readOnly
                              className="w-12 bg-zinc-900 border border-zinc-800 rounded text-center py-1 text-[11px] text-zinc-500 font-bold"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Calculations parameters Section */}
                      <div className="space-y-3 bg-black/40 p-4 border border-zinc-900 rounded-xl">
                        <strong className="text-cyan-400 block tracking-wide uppercase text-[10px] font-black">Calculation & Basis Rules</strong>
                        
                        <div className="grid grid-cols-2 gap-3.5">
                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-550 block uppercase font-bold">Calculation Basis</label>
                            <input 
                              type="text" 
                              placeholder="e.g. Core Net Receipts" 
                              value={calculationBasis} 
                              onChange={(e) => setCalculationBasis(e.target.value)} 
                              className="w-full bg-black border border-zinc-805 text-zinc-100 rounded px-2.5 py-1 text-[11px] focus:border-cyan-500"
                            />
                          </div>
                          
                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-550 block uppercase font-bold">Time Period</label>
                            <input 
                              type="text" 
                              placeholder="e.g. Q2 2026 Plan" 
                              value={timePeriod} 
                              onChange={(e) => setTimePeriod(e.target.value)} 
                              className="w-full bg-black border border-zinc-805 text-zinc-100 rounded px-2.5 py-1 text-[11px] focus:border-cyan-500"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-550 block uppercase font-bold">Contribution Cap</label>
                            <input 
                              type="text" 
                              placeholder="e.g. Unlimited" 
                              value={contributionCap} 
                              onChange={(e) => setContributionCap(e.target.value)} 
                              className="w-full bg-black border border-zinc-805 text-zinc-100 rounded px-2.5 py-1 text-[11px] focus:border-cyan-500"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-550 block uppercase font-bold">VAT Excluded</label>
                            <select 
                              value={vatExcluded} 
                              onChange={(e) => setVatExcluded(e.target.value)} 
                              className="w-full bg-black border border-zinc-805 text-zinc-100 rounded px-2 py-1 text-[11px]"
                            >
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-550 block uppercase font-bold">Refunds Excluded</label>
                            <select 
                              value={refundsExcluded} 
                              onChange={(e) => setRefundsExcluded(e.target.value)} 
                              className="w-full bg-black border border-zinc-850 text-zinc-100 rounded px-2 py-1 text-[11px]"
                            >
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-550 block uppercase font-bold">Chargebacks Excluded</label>
                            <select 
                              value={chargebacksExcluded} 
                              onChange={(e) => setChargebacksExcluded(e.target.value)} 
                              className="w-full bg-black border border-[#222] text-zinc-100 rounded px-2 py-1 text-[11px]"
                            >
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2.5">
                          <div className="flex items-center gap-2">
                            <input 
                              type="checkbox" 
                              id="public_disclosure" 
                              checked={publicDisclosureEnabled} 
                              onChange={(e) => setPublicDisclosureEnabled(e.target.checked)} 
                              className="bg-black border border-zinc-800 rounded text-cyan-500 w-3.5 h-3.5 cursor-pointer"
                            />
                            <label htmlFor="public_disclosure" className="text-[9.5px] text-zinc-400 select-none cursor-pointer uppercase font-bold">Enable Public Disclosure</label>
                          </div>

                          <div className="flex-1 flex items-center gap-2 justify-end">
                            <span className="text-[9px] text-zinc-550 uppercase font-bold">Audit Status:</span>
                            <select 
                              value={communityReviewStatus} 
                              onChange={(e) => setCommunityReviewStatus(e.target.value)} 
                              className="bg-black border border-zinc-800 rounded px-2 py-1 text-[10px] text-[#e840f3] font-black"
                            >
                              <option value="Pending">Pending</option>
                              <option value="Approved">Approved</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between items-center bg-[#010a12]/80 p-3.5 border border-zinc-900 rounded-xl mt-2 gap-3 text-[10px]">
                      <span className="text-slate-400 leading-normal">Fill in names (Dunfermline Core, Global Found., United Kingdom) and details to approve and launch community claims.</span>
                      <button 
                        onClick={() => {
                          setBeneficiary1Name("Fife Regional Community Initiative");
                          setBeneficiary2Name("Open Source Tech Access resource");
                          setBeneficiary3Name("UK Veterans Technological Adaptation Support");
                          setCalculationBasis("Eligible Net Receipts");
                          setTimePeriod("Q2 2026 Plan");
                          setContributionCap("Unlimited");
                          setPublicDisclosureEnabled(true);
                          setCommunityReviewStatus("Approved");
                          showToastMsg("Platform parameters configured and approved!", "success");
                        }}
                        className="px-4 py-1.5 bg-[#e840f3] hover:bg-[#e840f3]/90 text-white font-extrabold uppercase rounded active:scale-95 transition cursor-pointer text-[9.5px] min-w-max"
                      >
                        Auto-complete & Approve Config
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* TAB: EXPORTS - QuickBooks & Xero style */}
            {activeTab === 'EXPORTS' && (
              <motion.div
                key="tab-exports"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-6"
              >
                <div className="flex justify-between items-center border-b border-white/5 pb-4">
                  <div>
                    <h3 className="text-lg font-black font-sans uppercase tracking-tight flex items-center gap-2">
                      <Download size={18} className="text-yellow-400" />
                      QuickBooks & Xero style Export Center
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">
                      Export structured CSV, XLSX, and JSON files ready for accountant ingestion.
                    </p>
                  </div>
                </div>

                {/* Platform compliance safe warning */}
                <div className="p-4 rounded-xl bg-amber-950/20 border border-amber-500/20 text-xs text-left font-mono text-amber-400 leading-relaxed">
                  ⚠️ <strong className="text-white block font-bold">HMRC filing warning note:</strong>
                  Direct online submission to the HMRC VAT Gateway is currently in Sandbox/Developer mode. Submissions are disabled in public preview to prevent accidental double-reporting. Download package directly and route to your designated chartered accountant for review.
                </div>

                {/* Grid list of accounting packages */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-left font-mono">
                  <div className="p-4 bg-[#02050b] border border-zinc-900 rounded-2xl flex flex-col justify-between space-y-4">
                    <div className="space-y-1">
                      <span className="text-[11px] font-extrabold text-[#00ffd2] uppercase">Invoice Register</span>
                      <p className="text-[9px] text-zinc-500 leading-relaxed">Sequential invoice listings, payment date-lines, and tax calculations.</p>
                    </div>
                    <button
                      onClick={() => triggerMockExport('CSV', 'invoices')}
                      className="w-full text-center py-2 rounded-xl bg-[#00ffd2]/10 hover:bg-[#00ffd2]/20 border border-[#00ffd2]/30 text-white text-[9px] font-bold uppercase cursor-pointer"
                    >
                      Export CSV Package
                    </button>
                  </div>

                  <div className="p-4 bg-[#02050b] border border-zinc-900 rounded-2xl flex flex-col justify-between space-y-4">
                    <div className="space-y-1">
                      <span className="text-[11px] font-extrabold text-amber-500 uppercase">Payment Reconciles</span>
                      <p className="text-[9px] text-zinc-500 leading-relaxed">Reconciliation log balancing Stripe checkout tokens against invoices.</p>
                    </div>
                    <button
                      onClick={() => triggerMockExport('XLSX', 'payments_reconciles')}
                      className="w-full text-center py-2 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-white text-[9px] font-bold uppercase cursor-pointer"
                    >
                      Export XLSX Sheet
                    </button>
                  </div>

                  <div className="p-4 bg-[#02050b] border border-zinc-900 rounded-2xl flex flex-col justify-between space-y-4">
                    <div className="space-y-1">
                      <span className="text-[11px] font-extrabold text-[#e840f3] uppercase">Accounting Journals</span>
                      <p className="text-[9px] text-zinc-500 leading-relaxed">Double-entry General Ledger journals with transaction IDs.</p>
                    </div>
                    <button
                      onClick={() => triggerMockExport('PDF', 'accounting_journals_summary')}
                      className="w-full text-center py-2 rounded-xl bg-[#e840f3]/10 hover:bg-[#e840f3]/25 border border-[#e840f3]/30 text-white text-[9px] font-bold uppercase cursor-pointer"
                    >
                      Export PDF Summary
                    </button>
                  </div>

                  <div className="p-4 bg-[#02050b] border border-zinc-900 rounded-2xl flex flex-col justify-between space-y-4">
                    <div className="space-y-1">
                      <span className="text-[11px] font-extrabold text-[#00e5ff] uppercase">Developer JSON Audit</span>
                      <p className="text-[9px] text-zinc-500 leading-relaxed">Hex raw ledger dump with cryptographic hashes for integrity sync.</p>
                    </div>
                    <button
                      onClick={() => triggerMockExport('JSON', 'ledger_sha_audit')}
                      className="w-full text-center py-2 rounded-xl bg-[#00e5ff]/10 hover:bg-[#00e5ff]/20 border border-[#00e5ff]/30 text-white text-[9px] font-bold uppercase cursor-pointer"
                    >
                      Export Raw JSON
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* TAB: MTD HMRC GATEWAY */}
            {activeTab === 'MTD_GATEWAY' && (
              <motion.div
                key="tab-mtd-gateway"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-6 text-left"
              >
                <div className="flex justify-between items-center border-b border-white/5 pb-4">
                  <div>
                    <h3 className="text-lg font-black font-sans uppercase tracking-tight flex items-center gap-2">
                      <Terminal size={18} className="text-[#00ffd2]" />
                      Nexus HMRC Gateway
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">
                      UK Making Tax Digital sandbox link and company tax identity hub.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-zinc-400">Gateway Status:</span>
                    <span className="px-2.5 py-0.5 rounded font-black font-mono text-[8.5px] uppercase border bg-[#02050b] border-yellow-500/30 text-yellow-550">
                      Sandbox / Planning / Not Live
                    </span>
                  </div>
                </div>

                {/* Public status notice */}
                <div className="p-4 bg-zinc-950/80 border border-zinc-900 rounded-2xl text-xs font-mono text-left leading-relaxed text-zinc-400">
                  ⚠️ <strong className="text-white">Public Status:</strong> HMRC filing is not active in public preview. This software serves for internal record preparation and accountant-ready summaries.
                </div>

                {/* Admin Warning panel */}
                <div className="p-4 rounded-xl bg-amber-950/20 border border-amber-500/20 text-xs font-mono text-amber-400 text-left leading-relaxed">
                  ⚠️ <strong className="text-white block font-bold mb-1">Administrative Enforcement Warning:</strong>
                  Live HMRC submissions require corporate HMRC developer credentials, multi-factor OAuth authorisation, comprehensive sandboxed dry-run testing, and certified professional review before live activation.
                </div>

                {/* Modules grid */}
                <div className="space-y-4">
                  <h4 className="text-xs uppercase font-mono tracking-widest text-[#00ffd2] font-black">[ HMRC Sandbox Gateway Modules ]</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    {/* VAT MTD Sandbox Bridge */}
                    <div className="p-4 rounded-xl border border-white/5 bg-black/40 space-y-2">
                      <div className="flex justify-between items-center">
                        <strong className="text-white uppercase tracking-wider text-[11px] font-bold">VAT MTD Sandbox Bridge</strong>
                        <span className="text-[8px] font-mono font-bold uppercase bg-cyan-950/40 border border-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded">
                          Configured
                        </span>
                      </div>
                      <p className="text-zinc-450 text-[10px] leading-normal font-mono">
                        Making Tax Digital (MTD) VAT returns pipeline. Computes dynamic liability fields from adjusted journals.
                      </p>
                      <button 
                        onClick={() => showToastMsg("VAT MTD Bridge sandbox handshake verified.", "info")}
                        className="text-[9px] font-bold text-cyan-400 hover:underline uppercase inline-block pt-1 cursor-pointer"
                      >
                        Ping Sandboxed Bridge &rarr;
                      </button>
                    </div>

                    {/* Corporation Tax CT600 Preparation */}
                    <div className="p-4 rounded-xl border border-white/5 bg-black/40 space-y-2">
                      <div className="flex justify-between items-center">
                        <strong className="text-white uppercase tracking-wider text-[11px] font-bold">Corporation Tax CT600 Prep</strong>
                        <span className="text-[8px] font-mono font-bold uppercase bg-yellow-950/40 border border-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">
                          Sandbox
                        </span>
                      </div>
                      <p className="text-zinc-450 text-[10px] leading-normal font-mono">
                        Compiles net trading profits and capital allowance adjustments into standard UK Form CT600 structure.
                      </p>
                      <button 
                        onClick={() => showToastMsg("Form CT600 draft ledger compiled for exports.", "success")}
                        className="text-[9px] font-bold text-yellow-400 hover:underline uppercase inline-block pt-1 cursor-pointer"
                      >
                        Draft CT600 Structure &rarr;
                      </button>
                    </div>

                    {/* Self Assessment Readiness */}
                    <div className="p-4 rounded-xl border border-white/5 bg-black/40 space-y-2">
                      <div className="flex justify-between items-center">
                        <strong className="text-white uppercase tracking-wider text-[11px] font-bold">Self Assessment Readiness</strong>
                        <span className="text-[8px] font-mono font-bold uppercase bg-emerald-950/40 border border-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                          Active Recs
                        </span>
                      </div>
                      <p className="text-zinc-450 text-[10px] leading-normal font-mono">
                        Aggregates dividend vouchers, owner benefits under P11D, and allowable business expenses for individual return filing.
                      </p>
                      <button 
                        onClick={() => showToastMsg("Self Assessment parameters extracted mapping OK.", "info")}
                        className="text-[9px] font-bold text-emerald-400 hover:underline uppercase inline-block pt-1 cursor-pointer"
                      >
                        Compile Personal Mapping &rarr;
                      </button>
                    </div>

                    {/* Payroll/PAYE Readiness */}
                    <div className="p-4 rounded-xl border border-white/5 bg-black/40 space-y-2">
                      <div className="flex justify-between items-center">
                        <strong className="text-white uppercase tracking-wider text-[11px] font-bold">Payroll/PAYE Readiness</strong>
                        <span className="text-[8px] font-mono font-bold uppercase bg-purple-950/40 border border-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                          Ready
                        </span>
                      </div>
                      <p className="text-zinc-450 text-[10px] leading-normal font-mono">
                        Records statutory deductions, national ins. bands, student loan codes, and director primary thresholds.
                      </p>
                      <button 
                        onClick={() => showToastMsg("Payroll matrices structured for accountant export.", "success")}
                        className="text-[9px] font-bold text-purple-400 hover:underline uppercase inline-block pt-1 cursor-pointer"
                      >
                        Map PAYE Deductions &rarr;
                      </button>
                    </div>

                    {/* Accountant Export Module */}
                    <div className="p-4 rounded-xl border border-white/5 bg-black/40 space-y-2">
                      <div className="flex justify-between items-center">
                        <strong className="text-white uppercase tracking-wider text-[11px] font-bold">Accountant Export Gateway</strong>
                        <span className="text-[8px] font-mono font-bold uppercase bg-indigo-950/40 border border-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded">
                          Integrated
                        </span>
                      </div>
                      <p className="text-zinc-450 text-[10px] leading-normal font-mono">
                        Formats general ledger parameters into standard XBRL and CSV templates mapped to chartered accountant software.
                      </p>
                      <button 
                        onClick={() => showToastMsg("XBRL general ledger template download compiled.", "success")}
                        className="text-[9px] font-bold text-indigo-400 hover:underline uppercase inline-block pt-1 cursor-pointer"
                      >
                        Generate Format Specs &rarr;
                      </button>
                    </div>

                    {/* HMRC OAuth Placeholder */}
                    <div className="p-4 rounded-xl border border-[#00e5ff]/10 bg-black/60 space-y-2">
                      <div className="flex justify-between items-center">
                        <strong className="text-[#00e5ff] uppercase tracking-wider text-[11px] font-bold">HMRC OAuth Handler Placeholder</strong>
                        <span className="text-[8px] font-mono font-bold uppercase bg-[#00e5ff]/10 border border-[#00e5ff]/20 text-white px-2 py-0.5 rounded animate-pulse">
                          Sandboxed
                        </span>
                      </div>
                      <p className="text-zinc-450 text-[10px] leading-normal font-mono">
                        Pre-configured OAuth handshake parameters. Redirect link points to standard sandbox gateway environment.
                      </p>
                      <button 
                        onClick={() => showToastMsg("Interactive Token exchange simulated in Sandbox mode. Tokens expire in 3600s.", "info")}
                        className="text-[9px] font-bold text-cyan-400 hover:underline uppercase inline-block pt-1 cursor-pointer"
                      >
                        Simulate Handshake Authentication &rarr;
                      </button>
                    </div>

                    {/* Government Gateway Authorisation Placeholder */}
                    <div className="p-4 rounded-xl border border-[#e840f3]/10 bg-black/60 space-y-2 md:col-span-2">
                      <div className="flex justify-between items-center border-b border-zinc-900 pb-1.5 mb-1.5">
                        <strong className="text-white uppercase tracking-wider text-[11.5px] font-black flex items-center gap-1">
                          <Lock size={12} className="text-[#e840f3]" />
                          Government Gateway Authorisation Credentials (Simulated Boundary)
                        </strong>
                        <span className="text-[8px] font-mono font-bold uppercase bg-stone-900 border border-stone-750 text-stone-400 px-2 py-0.5 rounded">
                          Mock Gateway Active
                        </span>
                      </div>
                      <p className="text-zinc-450 text-[10px] leading-normal font-mono">
                        Authenticates multi-service accounts for VAT, PAYE and Corporation Tax. Live client-side secrets are strictly prohibited from storing in frontend client code bundles.
                      </p>
                      <div className="flex gap-2 pt-1 font-mono text-[9px]">
                        <button 
                          onClick={() => showToastMsg("Simulated Gov Gateway handshake success. Mapped services: VAT, Corp Tax.", "success")}
                          className="px-3 py-1 bg-zinc-900 border border-zinc-805 text-zinc-350 rounded hover:bg-zinc-800"
                        >
                          Trigger Simulated Handshake
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

                {/* SECTION: COMPANY TAX IDENTITY STORAGE (COMPANY UTR SAFETY) */}
                <div className="p-6 bg-zinc-950/90 border border-zinc-805 rounded-2xl space-y-5 font-mono text-zinc-350 text-xs">
                  
                  {/* Security Policy Header */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-zinc-900 pb-3 gap-2">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="text-yellow-500 w-5 h-5" />
                      <div>
                        <strong className="text-sm uppercase text-white tracking-wider font-sans font-black">Company Tax Identity Storage</strong>
                        <p className="text-[10px] text-zinc-550 lowercase">Complies with strict UTR security and non-extraction guidelines.</p>
                      </div>
                    </div>
                    <span className="px-2.5 py-0.5 rounded font-black font-mono text-[8px] uppercase bg-emerald-950/30 border border-emerald-500/20 text-emerald-400 min-w-max self-start md:self-center">
                      Security Policy Verified
                    </span>
                  </div>

                  {/* Anti-leak Warning */}
                  <div className="p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl text-[10.5px] leading-relaxed text-zinc-400 space-y-1.5">
                    <strong className="text-white uppercase text-[10px] block font-black">🔒 Compliance & Safe Cryptography Standards:</strong>
                    <ul className="list-disc list-inside space-y-0.5 text-zinc-450">
                      <li>Never show Corporation Tax UTR in plaintext.</li>
                      <li>Never write or store UTR values directly inside the frontend source code.</li>
                      <li>Never expose sensitive tax descriptors in debugger/terminal console logs.</li>
                      <li>Never package sensitive tax identifier codes inside general public exports/downloads.</li>
                      <li>Always store database-backed or secure ledger key values on secure servers, masking on-screen displays.</li>
                    </ul>
                  </div>

                  {/* Identity Detail Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    {/* Visual Display */}
                    <div className="bg-black/60 p-4 border border-zinc-900 rounded-xl space-y-3">
                      <strong className="text-[#00ffd2] block uppercase tracking-wider text-[10px] font-black border-b border-zinc-900 pb-1.5">Visual Identity Matrix</strong>
                      
                      <div className="space-y-2 text-[11px]">
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Legal Org Name:</span>
                          <span className="text-white font-bold">{companyLegalName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Company Standard ID:</span>
                          <span className="text-white font-mono">{companyNumber}</span>
                        </div>
                        <div className="flex justify-between items-center text-[#e840f3] font-bold">
                          <span>Corporation Tax UTR:</span>
                          <span className="font-mono text-xs tracking-widest">
                            ••••••••{companyUTR.slice(-4)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-550">Gov Gateway Service:</span>
                          <span className="text-zinc-350">{govGatewayStatus}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-550">Corp Tax Enrolment:</span>
                          <span className="text-zinc-350">{corpTaxEnrolment}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-550">VAT Registration:</span>
                          <span className="text-zinc-350">{vatRegStatus}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-550">PAYE Registration:</span>
                          <span className="text-zinc-350">{payeRegStatus}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-550">Self Assessment:</span>
                          <span className="text-zinc-350">{selfAssessmentStatus}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-550">Accountant validation:</span>
                          <span className="text-cyan-400 font-bold">{accountantReviewStatus}</span>
                        </div>
                      </div>
                    </div>

                    {/* ONLY VISIBLE TO ADMINS (EDIT PANEL) */}
                    {(activeRole === 'ADMIN' || activeRole === 'SUPER_ADMIN') ? (
                      <div className="bg-black/40 p-4 border border-zinc-900 rounded-xl space-y-3.5">
                        <strong className="text-yellow-500 block uppercase tracking-wider text-[10px] font-black border-b border-zinc-800 pb-1.5">Administrative Secure Customizer</strong>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                          <div className="space-y-1 sm:col-span-2">
                            <label className="text-[9px] text-zinc-500 block uppercase font-bold">Legal Company Name</label>
                            <input 
                              type="text" 
                              value={companyLegalName} 
                              onChange={(e) => setCompanyLegalName(e.target.value)} 
                              className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1 text-[11px]"
                            />
                          </div>
                          
                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-500 block uppercase font-bold">Company Number</label>
                            <input 
                              type="text" 
                              value={companyNumber} 
                              onChange={(e) => setCompanyNumber(e.target.value)} 
                              className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1 text-[11px]"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-500 block uppercase font-bold">Corporation Tax UTR (Raw)</label>
                            <input 
                              type="password" 
                              value={companyUTR} 
                              onChange={(e) => setCompanyUTR(e.target.value)} 
                              className="w-full bg-black border border-zinc-800 text-yellow-450 rounded px-2.5 py-1 text-[11px] font-bold"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-500 block uppercase font-bold">Gov Gateway State</label>
                            <input 
                              type="text" 
                              value={govGatewayStatus} 
                              onChange={(e) => setGovGatewayStatus(e.target.value)} 
                              className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1 text-[11px]"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-500 block uppercase font-bold">Corp Tax Enrolment</label>
                            <input 
                              type="text" 
                              value={corpTaxEnrolment} 
                              onChange={(e) => setCorpTaxEnrolment(e.target.value)} 
                              className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1 text-[11px]"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-500 block uppercase font-bold">VAT Status</label>
                            <input 
                              type="text" 
                              value={vatRegStatus} 
                              onChange={(e) => setVatRegStatus(e.target.value)} 
                              className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1 text-[11px]"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-zinc-500 block uppercase font-bold">Accountant Review</label>
                            <select 
                              value={accountantReviewStatus} 
                              onChange={(e) => setAccountantReviewStatus(e.target.value)} 
                              className="w-full bg-black border border-zinc-800 text-[#00ffd2] font-black rounded px-2 py-1 text-[11px]"
                            >
                              <option value="Review Required">Review Required</option>
                              <option value="Validated">Validated</option>
                            </select>
                          </div>
                        </div>

                        <div className="bg-[#0e0701] p-2.5 rounded text-[9.5px] border border-orange-500/20 text-orange-400">
                          ⚠️ Sensitive fields like Corporation Tax UTR are masked inside our visual layouts immediately. Plaintext coordinates are exclusively accessible in isolated session vaults.
                        </div>
                      </div>
                    ) : (
                      <div className="bg-zinc-950/40 p-4 border border-zinc-900 rounded-xl flex items-center justify-center text-center text-zinc-500 font-mono text-[10px]">
                        Secure administration customizer panels are restricted. Connect your supervisor credential keys to adjust system identity descriptors.
                      </div>
                    )}

                  </div>

                </div>
              </motion.div>
            )}

            {/* TAB: RETENTION REVIEWS */}
            {activeTab === 'RETENTION_REVIEWS' && (
              <motion.div
                key="tab-retention"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-6 text-left"
              >
                <div className="border-b border-white/5 pb-4">
                   <h3 className="text-lg font-black font-sans uppercase tracking-tight flex items-center gap-2">
                      <Lock size={18} className="text-[#e840f3]" />
                      Accounting Record Retention Policy Directive
                   </h3>
                   <p className="text-xs text-slate-400 mt-0.5 font-mono">
                      Statutory criteria for enterprise record storage duration.
                   </p>
                </div>

                {/* Key directives */}
                <div className="space-y-4 font-mono text-[11px] leading-relaxed text-zinc-350">
                  <div className="p-4 bg-zinc-950/80 border border-zinc-900 rounded-xl">
                    <strong className="text-white uppercase block text-[10px] mb-1">📅 6-Year Retention Safe Rule</strong>
                    Under UK company rules, invoices, receipts, payments, refunds, and general ledgers metadata must be preserved for <strong className="text-[#00ffd2]">at least 6 years</strong> from the end of the relevant tax period.
                  </div>

                  <div className="p-4 bg-zinc-950/80 border border-zinc-900 rounded-xl">
                    <strong className="text-white uppercase block text-[10px] mb-1">🔐 Privacy vs. Financial Integrity</strong>
                    If an end-user requests complete account erasure, transactional billing ledgers and payment receipts are preserved with personal details anonymised. General accounting journals are kept intact to secure continuous tax-audit trails perfectly.
                  </div>
                </div>
              </motion.div>
            )}

            {/* PUBLIC VISITOR TABS */}
            {activeTab === 'PRICING_TIERS' && (
              <motion.div
                key="tab-public-pricing"
                initial={{ opacity: 0, scale: 0.99 }}
                animate={{ opacity: 1 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md text-left space-y-6"
              >
                <h3 className="text-lg font-bold text-white uppercase tracking-tight">SaaS Subscription Pricing Plans</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 font-mono text-zinc-350 text-[11px]">
                  <div className="p-5 bg-zinc-950/80 border border-zinc-900 rounded-2xl space-y-3">
                    <strong className="text-cyan-400 text-xs block font-black">Bronze Star Member</strong>
                    <p className="text-zinc-500">Perfect for single developer testing environments.</p>
                    <div className="text-lg font-bold text-white">£49.00 / month</div>
                  </div>
                  <div className="p-5 bg-zinc-950/80 border border-zinc-900 rounded-2xl space-y-3">
                    <strong className="text-[#e840f3] text-xs block font-black">Zenith Executive Pro</strong>
                    <p className="text-zinc-500">Our primary plan for scaling regional business workflows.</p>
                    <div className="text-lg font-bold text-white">£149.00 / month</div>
                  </div>
                  <div className="p-5 bg-zinc-950/80 border border-emerald-500/20 rounded-2xl space-y-3">
                    <strong className="text-amber-400 text-xs block font-black">VIP Lifetime NexoraX</strong>
                    <p className="text-zinc-500">Full 18-sector server grid deployment with absolute access.</p>
                    <div className="text-lg font-bold text-white">£1,200.00 / setup</div>
                  </div>
                </div>
              </motion.div>
            )}

             {activeTab === 'PRIVACY_TERMS' && (
               <motion.div
                 key="tab-public-privacy"
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 className="space-y-4"
               >
                 <NexusPrivacyConsentCenter activeRole={activeRole} />
               </motion.div>
             )}

            {activeTab === 'COMMUNITY_DISCLOSURE' && (
              <motion.div
                key="tab-public-community"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md text-left font-mono text-[10px] text-zinc-400 space-y-4 animate-pulse"
              >
                <h3 className="text-md font-bold text-[#e840f3] uppercase">📢 3% Eligible Net Receipts Allocation Split</h3>
                <p>
                  When public configurations are finalised and reviewed by a chartered accountant, 
                  NexoraX allocates 3% of eligible net receipts to approved regional initiatives, starting with our core Dunfermline community digitisation initiatives.
                </p>
              </motion.div>
            )}

            {/* CLIENT SPECIFIC TABS */}
            {activeTab === 'CLIENT_INVOICES' && (
              <motion.div
                key="tab-client-invoices"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-4"
              >
                <h3 className="text-md font-bold text-white uppercase tracking-wider text-left">📑 My Paid Invoices & Receipts Statement</h3>
                <div className="space-y-3 font-mono text-[10px] text-left">
                  {invoices.slice(0, 2).map((inv) => (
                    <div key={inv.invoice_number} className="p-3 bg-zinc-950/80 border border-zinc-900 rounded-xl flex justify-between items-center">
                      <div>
                        <strong className="text-white">{inv.invoice_number}</strong>
                        <span className="block text-zinc-500 text-[8px]">{inv.product_service} - {inv.billing_period}</span>
                      </div>
                      <div className="text-right">
                        <strong className="text-emerald-400">£{inv.total.toFixed(2)} PAID</strong>
                        <a href={inv.receipt_link} target="_blank" rel="noreferrer" className="block text-cyan-400 text-[8px] hover:underline mt-0.5">Stripe Receipt</a>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === 'SEARCH_CREDITS_STATEMENT' && (
              <motion.div
                key="tab-client-credits"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md space-y-4 text-left"
              >
                <h3 className="text-md font-bold text-[#00ffd2] uppercase tracking-wider">🔑 My Search Credits ledger statement</h3>
                <div className="bg-[#02050b] p-4 border border-zinc-90 w-72 rounded-2xl font-mono text-[10px] space-y-2">
                  <span className="text-zinc-500">Active Search Credits Balance:</span>
                  <strong className="text-base text-white block">{searchCreditsBalance} credits</strong>
                  <span className="text-zinc-650 text-[7.5px] block leading-relaxed">Failed search queries or duplicate cached hits do not consume units. Standard tariff: £0.01 per valid sweep action.</span>
                </div>
              </motion.div>
            )}

            {activeTab === 'METERING_MAPPING' && (
              <motion.div
                key="tab-client-mapping"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-black/60 border border-white/10 rounded-2xl p-6 backdrop-blur-md text-left font-mono text-[10px] text-zinc-400 space-y-4"
              >
                <h3 className="text-md font-bold text-white uppercase">[ Client consumption index ]</h3>
                <p>
                  You are viewing active service usage mapping for workspace wksp_alpha_991. 
                  All metric points are recorded on server-side nodes in real time. Standard tariff limits are computed daily under direct supervisor compliance guidelines.
                </p>
              </motion.div>
            )}

            {activeTab === 'TAX_COMPLIANCE_HUB' && (
              <motion.div
                key="tab-tax-compliance"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <NexusTaxReadinessHub />
              </motion.div>
            )}

          </AnimatePresence>
        </div>

      </div>

      {/* Floating System Notification strip */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 15 }}
          className={`fixed bottom-6 right-6 px-4 py-2 text-[10px] font-mono tracking-widest uppercase font-black rounded-xl border shadow-lg z-50 flex items-center gap-2 ${
            toast.type === 'success' ? 'bg-[#031d10] border-emerald-500/40 text-emerald-400' :
            toast.type === 'error' ? 'bg-[#220710] border-rose-500/40 text-rose-450' :
            'bg-[#061825] border-cyan-500/40 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
          }`}
        >
          <Activity size={12} className={toast.type === 'info' ? 'animate-pulse' : ''} />
          <span>{toast.message}</span>
        </motion.div>
      )}

      {/* Empty space filler spacer */}
      <div className="h-6"></div>

    </div>
  );
}
